import React, { useEffect, useState, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { useVideoCall } from '../contexts/VideoCallContext';
import { FeedsClient } from '@stream-io/feeds-react-sdk';
import { StreamChat } from 'stream-chat';
import {
  Chat,
  Channel,
  ChannelList,
  Window,
  ChannelHeader,
  MessageList,
  MessageInput,
  Thread,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
  MessageSimple,
} from 'stream-chat-react';
import 'stream-chat-react/dist/css/v2/index.css';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar as CalendarUI } from './ui/calendar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  ArrowLeft, Loader2, Send, Heart, MessageCircle, Trash2, Image as ImageIcon, X,
  Users, MessageSquare, BarChart3, MessagesSquare, Video, Calendar, Pin, PinOff, Edit2, Plus,
  Pencil, Check, Bot, GraduationCap,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PollWidget, CreatePollModal } from './PollComponents';
import CapacitacionesProfesorWidget from './CapacitacionesProfesorWidget';
import GenesisAITab from './GenesisAITab';
import AvanceEstudiantes from './AvanceEstudiantes';

/**
 * Sala Docente — Espacio privado para admin + profesor.
 * Pestañas: Pizarra (feed) | Chat | Reuniones (próximamente) | Calendario (próximamente)
 */
const FEED_OWNER = 'area_profesores';
const FEED_FQ_ID = `user:${FEED_OWNER}`;
const CHAT_CHANNEL_ID = 'sala-docente-iba';

const SalaDocente = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { API, user } = useContext(AuthContext);
  const initialTab = searchParams.get('tab') || 'pizarra';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [info, setInfo] = useState(null);
  const [accessError, setAccessError] = useState(null);

  // Estado GLOBAL de la videollamada de Reunión de Profesores.
  // Vive a nivel SalaDocente para sobrevivir al cambio de tabs (sino el iframe
  // se desmonta al moverse a "Avance Estudiantes" y la llamada se corta).
  const [activeRoom, setActiveRoom] = useState(null); // {url, titulo}
  const [isRoomMinimized, setIsRoomMinimized] = useState(false);
  useEffect(() => {
    if (!activeRoom) setIsRoomMinimized(false);
  }, [activeRoom]);

  // === MediaRecorder: grabación local gratis de la reunión ===
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0); // segundos
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStreamRef = useRef(null);

  const startRecording = async () => {
    try {
      // Capturar pantalla (el usuario elige la pestaña con la reunión) + audio
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 30 },
        audio: true, // captura "Compartir audio de la pestaña" si el user lo marca
      });

      // Intentar también capturar el micrófono del host para que su voz quede.
      let combinedStream = displayStream;
      try {
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const ctx = new AudioContext();
        const dest = ctx.createMediaStreamDestination();
        // Mezclar audio de la pestaña (si existe) con el micrófono
        if (displayStream.getAudioTracks().length > 0) {
          ctx.createMediaStreamSource(displayStream).connect(dest);
        }
        ctx.createMediaStreamSource(micStream).connect(dest);
        combinedStream = new MediaStream([
          ...displayStream.getVideoTracks(),
          ...dest.stream.getAudioTracks(),
        ]);
      } catch (micErr) {
        console.warn('No se pudo capturar mic, sigo solo con audio de pestaña:', micErr?.message);
      }

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : 'video/webm';
      const recorder = new MediaRecorder(combinedStream, { mimeType, videoBitsPerSecond: 2_500_000 });

      recordedChunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recordedChunksRef.current.push(ev.data);
      };
      recorder.onstop = () => {
        try {
          const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          const fecha = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
          const titulo = (activeRoom?.titulo || 'reunion').replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 40);
          a.href = url;
          a.download = `IBA_${titulo}_${fecha}.webm`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 30_000);
          toast.success('✅ Grabación descargada a tu PC. Ya podés subirla a Google Drive si querés.');
        } catch (e) {
          console.error('Error al guardar grabación:', e);
          toast.error('Hubo un problema guardando la grabación');
        }
        recordedChunksRef.current = [];
      };

      // Si el usuario corta el screen share desde el navegador, finalizamos.
      displayStream.getVideoTracks()[0].addEventListener('ended', () => {
        stopRecording();
      });

      recordingStreamRef.current = combinedStream;
      mediaRecorderRef.current = recorder;
      recorder.start(2000); // chunks cada 2s

      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((s) => s + 1);
      }, 1000);

      toast.success('🔴 Grabación iniciada. Asegurate de elegir "Compartir audio de la pestaña".');
    } catch (e) {
      console.error('startRecording error:', e);
      if (e?.name === 'NotAllowedError') {
        toast.error('Cancelaste la captura de pantalla. Grabación no iniciada.');
      } else {
        toast.error('No se pudo iniciar la grabación: ' + (e?.message || ''));
      }
    }
  };

  const stopRecording = () => {
    try {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== 'inactive') {
        rec.stop();
      }
      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach((t) => t.stop());
        recordingStreamRef.current = null;
      }
    } catch (e) {
      console.warn('stopRecording error:', e?.message);
    } finally {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      setIsRecording(false);
      setRecordingDuration(0);
      mediaRecorderRef.current = null;
    }
  };

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (_e) {}
      }
      if (recordingStreamRef.current) {
        recordingStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // Si se cierra la reunión mientras se graba, finalizar grabación.
  useEffect(() => {
    if (!activeRoom && isRecording) {
      stopRecording();
    }
  }, [activeRoom, isRecording]);

  const fmtRecTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const switchTab = (t) => {
    setActiveTab(t);
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    if (t !== 'reuniones') next.delete('reunionId');
    setSearchParams(next, { replace: true });
  };

  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/area-profesores/info`, { headers: headersRef.current });
        setInfo(r.data);
      } catch (e) {
        if (e.response?.status === 403) setAccessError('Esta sección es solo para profesores y administradores.');
        else setAccessError('No se pudo cargar la Sala Docente');
      }
    })();
  }, [API, user]);

  if (accessError) {
    return (
      <div className="container mx-auto p-6 max-w-3xl">
        <Card>
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600">{accessError}</p>
            <Button onClick={() => navigate('/app')} className="mt-4" variant="outline">
              Volver
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className={`container mx-auto p-4 ${activeTab === 'avance' ? 'max-w-7xl' : 'max-w-4xl'}`}>
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={() => navigate('/app')}
          className="p-1.5 rounded-full hover:bg-gray-100 text-gray-600"
          data-testid="sala-docente-back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="bg-gradient-to-br from-indigo-500 to-blue-600 bg-clip-text text-transparent">
                Sala Docente
              </span>
            </h1>
            <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 border-0">Nuevo</Badge>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Espacio exclusivo para profesores y administradores · {info.miembros_count} miembros
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-100 flex overflow-x-auto" data-testid="sala-docente-tabs">
          <TabButton
            active={activeTab === 'pizarra'}
            onClick={() => switchTab('pizarra')}
            icon={<MessageSquare className="w-4 h-4" />}
            label="Anuncios"
            testId="tab-pizarra"
          />
          <TabButton
            active={activeTab === 'chat'}
            onClick={() => switchTab('chat')}
            icon={<MessagesSquare className="w-4 h-4" />}
            label="Chat"
            testId="tab-chat"
          />
          <TabButton
            active={activeTab === 'reuniones'}
            onClick={() => switchTab('reuniones')}
            icon={<Video className="w-4 h-4" />}
            label="Reuniones"
            testId="tab-reuniones"
          />
          <TabButton
            active={activeTab === 'calendario'}
            onClick={() => switchTab('calendario')}
            icon={<Calendar className="w-4 h-4" />}
            label="Calendario"
            testId="tab-calendario"
          />
          <TabButton
            active={activeTab === 'genesis'}
            onClick={() => switchTab('genesis')}
            icon={<Bot className="w-4 h-4" />}
            label="Genesis AI"
            testId="tab-genesis"
          />
          <TabButton
            active={activeTab === 'avance'}
            onClick={() => switchTab('avance')}
            icon={<GraduationCap className="w-4 h-4" />}
            label="Avance Estudiantes"
            testId="tab-avance"
          />
          <TabButton
            active={activeTab === 'documentos'}
            onClick={() => switchTab('documentos')}
            icon={<i className="fab fa-google-drive text-base" />}
            label="Documentos"
            testId="tab-documentos"
          />
        </div>

        <div className="p-0">
          {activeTab === 'pizarra' && <PizarraDocente API={API} user={user} info={info} />}
          {activeTab === 'chat' && <ChatTab API={API} />}
          {activeTab === 'reuniones' && (
            <ReunionesTab API={API} user={user} info={info}
              activeRoom={activeRoom}
              setActiveRoom={setActiveRoom}
            />
          )}
          {activeTab === 'calendario' && <CalendarioTab API={API} user={user} />}
          {activeTab === 'genesis' && (
            <div className="h-[calc(100dvh-260px)] min-h-[500px]">
              <GenesisAITab />
            </div>
          )}
          {activeTab === 'avance' && (
            <div className="p-2 sm:p-4">
              {/* Reutilizamos el componente completo de Avance Estudiantes,
                  con el ancho ampliado del container de Sala Docente. */}
              <AvanceEstudiantes />
            </div>
          )}
          {activeTab === 'documentos' && <DocumentosDriveTab />}
        </div>
      </div>

      {/* Overlay GLOBAL de la reunión de profesores — vive en SalaDocente para
          persistir al cambiar de tab. Soporta modo fullscreen y minimizado. */}
      {activeRoom && (
        <div
          className={
            isRoomMinimized
              ? "fixed bottom-4 right-4 z-[9998] bg-black rounded-2xl shadow-2xl border-2 border-emerald-500 flex flex-col overflow-hidden w-[420px] h-[280px]"
              : "fixed inset-0 z-[9998] bg-black flex flex-col"
          }
          style={isRoomMinimized ? { resize: 'both', overflow: 'hidden' } : { height: '100dvh', maxHeight: '100dvh' }}
          data-testid="reunion-active-room"
        >
          <div className={`flex items-center justify-between bg-gray-900 shrink-0 ${isRoomMinimized ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
            <div className="flex items-center gap-2 text-white min-w-0">
              <Video className={isRoomMinimized ? 'w-3.5 h-3.5 shrink-0' : 'w-4 h-4 shrink-0'} />
              <span className={`font-medium truncate ${isRoomMinimized ? 'text-xs' : 'text-sm'}`}>
                {isRoomMinimized ? 'Reunión' : `🎥 ${activeRoom.titulo}`}
              </span>
              {isRoomMinimized && (
                <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse shrink-0" title="En vivo" />
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0 ml-2">
              {/* Botón Grabar / Detener — solo modo expandido */}
              {!isRoomMinimized && (
                isRecording ? (
                  <button
                    onClick={stopRecording}
                    className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors px-3 py-1.5 text-sm font-semibold animate-pulse"
                    title="Detener grabación y descargar"
                    data-testid="reunion-stop-recording-btn"
                  >
                    <span className="inline-block w-2 h-2 bg-white rounded-full" />
                    <span>Detener · {fmtRecTime(recordingDuration)}</span>
                  </button>
                ) : (
                  <button
                    onClick={startRecording}
                    className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors px-3 py-1.5 text-sm"
                    title="Grabar la reunión localmente (se descargará a tu PC)"
                    data-testid="reunion-start-recording-btn"
                  >
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full" />
                    <span>Grabar</span>
                  </button>
                )
              )}
              {isRoomMinimized && isRecording && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold"
                  title={`Grabando · ${fmtRecTime(recordingDuration)}`}
                >
                  <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  REC
                </span>
              )}
              <button
                onClick={() => setIsRoomMinimized((m) => !m)}
                className={`flex items-center gap-1 bg-white/15 hover:bg-white/25 text-white rounded-lg transition-colors ${
                  isRoomMinimized ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                }`}
                title={isRoomMinimized ? 'Expandir a pantalla completa' : 'Minimizar (ver otras pestañas)'}
                data-testid={isRoomMinimized ? 'reunion-expand-btn' : 'reunion-minimize-btn'}
              >
                <i className={`fas ${isRoomMinimized ? 'fa-expand' : 'fa-compress'}`} />
                {!isRoomMinimized && <span>Minimizar</span>}
              </button>
              <button
                onClick={() => {
                  if (isRecording && !window.confirm('Hay una grabación en curso. ¿Querés detenerla y descargarla antes de salir?')) return;
                  if (isRecording) stopRecording();
                  setActiveRoom(null);
                }}
                className={`flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors ${
                  isRoomMinimized ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
                }`}
                data-testid="reunion-leave-btn"
              >
                <X className={isRoomMinimized ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
                {!isRoomMinimized && <span>Salir</span>}
              </button>
            </div>
          </div>
          <iframe
            src={activeRoom.url}
            allow="camera *; microphone *; fullscreen; speaker; display-capture; autoplay; clipboard-write; picture-in-picture"
            allowFullScreen
            className="flex-1 w-full border-0"
            style={{ minHeight: 0 }}
            title="Daily Sala Docente"
          />
        </div>
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, icon, label, comingSoon, testId }) => (
  <button
    onClick={onClick}
    className={`px-4 py-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
      active
        ? 'border-indigo-500 text-indigo-700 bg-indigo-50/50'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    {icon}
    {label}
    {comingSoon && <Badge variant="outline" className="text-[10px] px-1.5 py-0">Pronto</Badge>}
  </button>
);

const ComingSoon = ({ title }) => (
  <div className="p-12 text-center text-gray-500">
    <p className="text-base font-medium">{title}</p>
    <p className="text-sm mt-1">Disponible en la próxima actualización.</p>
  </div>
);

// ============================================================================
// Reuniones Tab — Crear/listar reuniones Daily con multi-select de invitados
// ============================================================================
const SALA_DOCENTE_DRIVE_FOLDER_ID = '1YVfAyGkIzJOzHhUC14clD05TxT4nW4fQ';
const SALA_DOCENTE_DRIVE_FOLDER_URL = `https://drive.google.com/drive/folders/${SALA_DOCENTE_DRIVE_FOLDER_ID}`;
const SALA_DOCENTE_DRIVE_EMBED_URL = `https://drive.google.com/embeddedfolderview?id=${SALA_DOCENTE_DRIVE_FOLDER_ID}#list`;

const DocumentosDriveTab = () => {
  return (
    <div className="p-3 sm:p-5" data-testid="documentos-drive-tab">
      <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border-l-4 border-blue-500 rounded-r-xl p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-gray-800 text-base sm:text-lg flex items-center gap-2">
            <i className="fab fa-google-drive text-blue-500" />
            Documentos compartidos de Sala Docente
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed">
            Profesores y administradores pueden <strong>subir, editar y descargar</strong> documentos de reuniones desde acá. Los archivos viven en Google&nbsp;Drive y los permisos ya están configurados (cualquiera con el link puede editar).
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 shrink-0">
          <a
            href={SALA_DOCENTE_DRIVE_FOLDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow text-sm"
            data-testid="documentos-open-drive-btn"
          >
            <i className="fas fa-up-right-from-square" />
            Abrir en Drive (subir / editar)
          </a>
          <button
            onClick={() => {
              try { navigator.clipboard.writeText(SALA_DOCENTE_DRIVE_FOLDER_URL); toast.success('Link copiado al portapapeles'); }
              catch { toast.error('No se pudo copiar el link'); }
            }}
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-semibold px-4 py-2 rounded-lg shadow text-sm border border-gray-200"
            data-testid="documentos-copy-link-btn"
          >
            <i className="fas fa-link" />
            Copiar link
          </button>
        </div>
      </div>

      {/* Embed de la carpeta (vista de archivos, no permite subir desde el iframe) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <iframe
          src={SALA_DOCENTE_DRIVE_EMBED_URL}
          title="Carpeta de Documentos — Sala Docente"
          className="w-full border-0"
          style={{ height: 'calc(100dvh - 320px)', minHeight: 420 }}
          data-testid="documentos-drive-iframe"
        />
      </div>

      <p className="text-xs text-gray-500 mt-3 text-center">
        💡 Para <strong>subir un archivo</strong>, apretá "Abrir en Drive" — desde Google Drive podés arrastrar archivos o usar el botón "Nuevo".
      </p>
    </div>
  );
};


const ReunionesTab = ({ API, user, info, activeRoom, setActiveRoom }) => {
  const [searchParams] = useSearchParams();
  const focusReunionId = searchParams.get('reunionId');
  const [reuniones, setReuniones] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingReunion, setEditingReunion] = useState(null);
  const [filter, setFilter] = useState('proximas'); // proximas | pasadas
  const [joiningId, setJoiningId] = useState(null);
  // activeRoom y setActiveRoom vienen ahora del componente padre (SalaDocente)
  // para que la reunión persista al cambiar de tab.
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, pRes] = await Promise.all([
        axios.get(`${API}/area-profesores/reuniones?estado=${filter}`, { headers: headersRef.current }),
        axios.get(`${API}/area-profesores/profesores-disponibles?incluir_estudiantes=true`, { headers: headersRef.current }),
      ]);
      setReuniones(rRes.data.reuniones || []);
      setProfesores(pRes.data.profesores || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API, filter]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleJoin = async (rId) => {
    setJoiningId(rId);
    try {
      const r = await axios.get(`${API}/area-profesores/reuniones/${rId}/join`, { headers: headersRef.current });
      const optimizedUrl = optimizeDailyUrlForMobile(r.data.room_url);
      setActiveRoom({ url: optimizedUrl, titulo: r.data.titulo });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo unir');
    } finally {
      setJoiningId(null);
    }
  };

  const handleCancel = async (rId) => {
    if (!window.confirm('¿Cancelar esta reunión? Se notificará a los invitados.')) return;
    try {
      await axios.delete(`${API}/area-profesores/reuniones/${rId}`, { headers: headersRef.current });
      toast.success('Reunión cancelada');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar');
    }
  };

  const handleRSVP = async (rId, status, reason) => {
    try {
      await axios.post(
        `${API}/area-profesores/reuniones/${rId}/rsvp`,
        { status, reason: reason || '' },
        { headers: headersRef.current }
      );
      const msg = { aceptado: '✅ Confirmaste tu asistencia', tal_vez: '🤔 Marcaste tu asistencia como tentativa', rechazado: '❌ Marcaste que no podrás asistir' }[status];
      toast.success(msg || 'Respuesta enviada');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar la respuesta');
    }
  };

  // Auto-RSVP cuando el usuario llega desde el email (con ?rsvp=aceptado|tal_vez|rechazado)
  const autoRsvpAppliedRef = useRef(false);
  useEffect(() => {
    if (autoRsvpAppliedRef.current) return;
    const rsvpQP = searchParams.get('rsvp');
    const rId = searchParams.get('reunionId');
    if (!rsvpQP || !rId || loading) return;
    // Validar valor
    const valid = ['aceptado', 'tal_vez', 'rechazado'].includes(rsvpQP);
    if (!valid) return;
    // Buscar la reunión
    const reunion = reuniones.find(r => r.id === rId);
    if (!reunion) return;
    // No aplicar si el usuario NO está invitado o ya respondió con ese status
    const invitado = (reunion.invitados_ids || []).includes(user?.id);
    if (!invitado) {
      toast.error('No estás invitado a esta reunión');
      autoRsvpAppliedRef.current = true;
      return;
    }
    const yaEsIgual = reunion.mi_rsvp?.status === rsvpQP;
    if (yaEsIgual) {
      autoRsvpAppliedRef.current = true;
      return;
    }
    autoRsvpAppliedRef.current = true;
    handleRSVP(rId, rsvpQP, rsvpQP === 'rechazado' ? '(respuesta desde email)' : '');
  }, [searchParams, reuniones, loading, user, handleRSVP]);

  // Scroll a la tarjeta cuando llega con ?reunionId=... (deep-link desde push)
  useEffect(() => {
    if (loading || !focusReunionId) return;
    // Damos tiempo a que el DOM renderice
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="reunion-card-${focusReunionId}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [loading, focusReunionId, reuniones]);

  if (activeRoom) {
    // El iframe de la reunión se renderiza a nivel SalaDocente (raíz) para
    // persistir al cambiar de tab y soportar el modo minimizado. Cuando hay
    // una reunión activa, esta pestaña no renderiza nada (queda en blanco
    // tras el iframe). Cuando se minimiza, la pestaña se ve normal.
    return (
      <div className="p-6 text-center text-gray-500">
        <Video className="w-12 h-12 mx-auto text-emerald-500 mb-3 animate-pulse" />
        <p className="font-semibold text-gray-700">Estás en una reunión activa</p>
        <p className="text-sm mt-1">Si la minimizaste, podés ver las otras pestañas y volver acá cuando quieras.</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button
            onClick={() => setFilter('proximas')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              filter === 'proximas' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-600'
            }`}
            data-testid="filter-proximas"
          >
            Próximas
          </button>
          <button
            onClick={() => setFilter('pasadas')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md ${
              filter === 'pasadas' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-600'
            }`}
            data-testid="filter-pasadas"
          >
            Pasadas
          </button>
        </div>
        <Button onClick={() => setShowCreate(true)} size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-nueva-reunion">
          <Video className="w-4 h-4 mr-1" /> Nueva reunión
        </Button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
      ) : reuniones.length === 0 ? (
        <Card className="bg-gray-50 border-dashed">
          <CardContent className="p-8 text-center text-gray-500">
            <Video className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">
              {filter === 'proximas' ? 'No hay reuniones próximas.' : 'No hay reuniones pasadas.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(() => {
            // Ordenar por fecha de reunión: próximas asc (más cercana primero),
            // pasadas desc (más reciente primero).
            const sorted = [...reuniones].sort((a, b) => {
              const da = new Date(a.fecha_hora_inicio || 0).getTime();
              const dbb = new Date(b.fecha_hora_inicio || 0).getTime();
              return filter === 'proximas' ? da - dbb : dbb - da;
            });
            // Identificar la "próxima a cumplirse": primera no-cancelada cuya
            // fecha sea ≥ ahora (solo aplica al filtro "proximas").
            const ahora = Date.now();
            const nextId = filter === 'proximas'
              ? (sorted.find(r =>
                  r.estado !== 'cancelada' &&
                  new Date(r.fecha_hora_inicio || 0).getTime() >= ahora
                )?.id || null)
              : null;
            return sorted.map((r) => (
              <ReunionCard
                key={r.id}
                reunion={r}
                currentUserId={user?.id}
                currentUserRole={user?.rol}
                highlighted={r.id === focusReunionId}
                isNext={r.id === nextId}
                joining={joiningId === r.id}
                onJoin={() => handleJoin(r.id)}
                onCancel={() => handleCancel(r.id)}
                onEdit={() => setEditingReunion(r)}
                onRSVP={handleRSVP}
                onRefresh={loadAll}
                isPast={filter === 'pasadas'}
              />
            ));
          })()}
        </div>
      )}

      {(showCreate || editingReunion) && (
        <CreateReunionModal
          isOpen
          onClose={() => { setShowCreate(false); setEditingReunion(null); }}
          onCreated={loadAll}
          API={API}
          profesores={profesores}
          currentUserId={user?.id}
          editing={editingReunion}
        />
      )}
    </div>
  );
};

const RSVP_STYLES = {
  aceptado: { bg: 'bg-emerald-500', ring: 'ring-emerald-400', icon: '✓', label: 'Asistirá', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50', border: 'border-emerald-300' },
  tal_vez: { bg: 'bg-amber-500', ring: 'ring-amber-400', icon: '?', label: 'Tal vez', textColor: 'text-amber-700', bgLight: 'bg-amber-50', border: 'border-amber-300' },
  rechazado: { bg: 'bg-red-500', ring: 'ring-red-400', icon: '✕', label: 'No asistirá', textColor: 'text-red-700', bgLight: 'bg-red-50', border: 'border-red-300' },
  pendiente: { bg: 'bg-gray-300', ring: 'ring-gray-300', icon: '⏳', label: 'Sin responder', textColor: 'text-gray-600', bgLight: 'bg-gray-50', border: 'border-gray-300' },
};

const InvitadoAvatar = ({ invitado, onClickAvatar, canRecordar, ultimoRecordatorio }) => {
  const status = invitado.status || 'pendiente';
  const sty = RSVP_STYLES[status] || RSVP_STYLES.pendiente;
  const clickable = canRecordar && status !== 'rechazado';

  // Human-readable "hace X"
  let hint = '';
  if (ultimoRecordatorio?.enviado_en) {
    const ms = Date.now() - new Date(ultimoRecordatorio.enviado_en).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 60) hint = `Recordado hace ${mins} min`;
    else if (mins < 1440) hint = `Recordado hace ${Math.floor(mins / 60)} h`;
    else hint = `Recordado hace ${Math.floor(mins / 1440)} d`;
  }
  const title = `${invitado.nombre} — ${sty.label}${invitado.reason ? ` (${invitado.reason})` : ''}${hint ? ` · ${hint}` : ''}${clickable ? ' · Click para enviar recordatorio' : ''}`;

  const inner = (
    <>
      {invitado.image ? (
        <img
          src={invitado.image}
          alt={invitado.nombre}
          className={`w-8 h-8 rounded-full object-cover ring-2 ${sty.ring}`}
          style={status === 'rechazado' ? { filter: 'grayscale(70%)', opacity: 0.65 } : undefined}
        />
      ) : (
        <div className={`w-8 h-8 rounded-full bg-indigo-200 text-indigo-700 text-xs font-bold flex items-center justify-center ring-2 ${sty.ring}`}>
          {(invitado.nombre || '?').slice(0, 1).toUpperCase()}
        </div>
      )}
      {/* Tachado para los que rechazaron */}
      {status === 'rechazado' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-full h-[2px] bg-red-600 rotate-45 origin-center"></div>
        </div>
      )}
      {/* Badge en esquina inferior derecha */}
      <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 ${sty.bg} text-white text-[8px] font-bold rounded-full flex items-center justify-center border-2 border-white`}>
        {sty.icon}
      </span>
      {/* Indicador de "ya recordado" */}
      {ultimoRecordatorio?.enviado_en && (
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white" title={hint} />
      )}
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        className="relative shrink-0 cursor-pointer hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded-full"
        title={title}
        onClick={() => onClickAvatar(invitado)}
        data-testid={`invitado-avatar-${invitado.id}`}
      >
        {inner}
      </button>
    );
  }
  return (
    <div className="relative shrink-0" title={title}>
      {inner}
    </div>
  );
};

const ReunionCard = ({ reunion, currentUserId, currentUserRole, highlighted, isNext, joining, onJoin, onCancel, onEdit, onRSVP, onRefresh, isPast }) => {
  const fecha = reunion.fecha_hora_inicio ? new Date(reunion.fecha_hora_inicio) : null;
  const inviCount = (reunion.invitados_ids || []).length;
  const puedeModificar =
    !isPast &&
    reunion.estado === 'activa' &&
    (reunion.organizador_id === currentUserId || currentUserRole === 'admin');

  const miRsvp = reunion.mi_rsvp;
  const miStatus = miRsvp?.status || (reunion.invitados_ids?.includes(currentUserId) ? 'pendiente' : null);
  const puedoRsvp = !isPast && reunion.estado === 'activa' && !reunion.soy_organizador && miStatus;

  const resumen = reunion.rsvps_resumen || {};
  const invitadosConRsvp = reunion.invitados_con_rsvp || [];
  const ultimoRecPorUsuario = reunion.ultimo_recordatorio_por_usuario || {};

  // Solo organizador o admin pueden enviar recordatorios
  const canRecordar =
    !isPast &&
    reunion.estado === 'activa' &&
    (reunion.organizador_id === currentUserId || currentUserRole === 'admin');
  const [recordando, setRecordando] = useState(false);

  const enviarRecordatorio = async (modo, userId = null) => {
    if (recordando) return;
    // Warning suave si ya se recordó a este usuario recientemente
    if (modo === 'individual' && userId) {
      const ult = ultimoRecPorUsuario[userId];
      if (ult?.enviado_en) {
        const horas = (Date.now() - new Date(ult.enviado_en).getTime()) / 3600000;
        if (horas < 6) {
          const proceed = window.confirm(
            `Este profesor ya recibió un recordatorio hace ${horas < 1 ? Math.floor(horas * 60) + ' min' : Math.floor(horas) + ' h'}.\n\n¿Enviar otro igual?`
          );
          if (!proceed) return;
        }
      }
    }
    setRecordando(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const body = { modo };
      if (modo === 'individual' && userId) body.user_id = userId;
      const r = await axios.post(`${API}/area-profesores/reuniones/${reunion.id}/recordar`, body);
      const enviados = r.data?.enviados || 0;
      const omitidos = (r.data?.omitidos || []).length;
      if (enviados > 0) {
        toast.success(`✅ Recordatorio enviado a ${enviados} ${enviados === 1 ? 'persona' : 'personas'}${omitidos ? ` (${omitidos} omitidos)` : ''}`);
      } else {
        toast.info('No había personas elegibles para recordar');
      }
      // Recargar reunión
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar recordatorio');
    } finally {
      setRecordando(false);
    }
  };

  const onAvatarClick = (invitado) => {
    const label = invitado.status === 'aceptado' ? 'un recordatorio de que tiene cita agendada' : 'una invitación a confirmar asistencia';
    if (!window.confirm(`¿Enviar a ${invitado.nombre} ${label}?\n\nEl mensaje llegará por email y notificación push.`)) return;
    enviarRecordatorio('individual', invitado.id);
  };

  // Razón al rechazar (modal-inline simple)
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState(miStatus === 'rechazado' ? (miRsvp?.reason || '') : '');
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);

  const respond = async (status) => {
    if (rsvpSubmitting) return;
    if (status === 'rechazado' && !showRejectReason) {
      // primero pedimos razón opcional
      setShowRejectReason(true);
      return;
    }
    setRsvpSubmitting(true);
    try {
      await onRSVP(reunion.id, status, status === 'rechazado' ? rejectReason.trim() : '');
      setShowRejectReason(false);
    } finally {
      setRsvpSubmitting(false);
    }
  };

  return (
    <Card
      className={`relative border shadow-sm transition-all ${
        isNext
          ? 'border-emerald-400 ring-2 ring-emerald-300 shadow-lg bg-gradient-to-br from-emerald-50/70 to-white'
          : highlighted
            ? 'border-indigo-400 ring-2 ring-indigo-200'
            : 'border-gray-100'
      }`}
      data-testid={`reunion-card-${reunion.id}`}
    >
      {isNext && (
        <div
          className="absolute -top-2 left-3 z-10 inline-flex items-center gap-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-bold tracking-wide uppercase px-2 py-0.5 rounded-full shadow-md ring-1 ring-emerald-200"
          data-testid={`reunion-next-badge-${reunion.id}`}
        >
          <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
          Próxima
        </div>
      )}
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-800 truncate">{reunion.titulo}</h3>
              {reunion.soy_organizador && <Badge variant="outline" className="text-[10px]">Organizo yo</Badge>}
              {reunion.estado === 'cancelada' && <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Cancelada</Badge>}
              {/* Mi propio estado RSVP — visible siempre que soy invitado */}
              {miStatus && !reunion.soy_organizador && (
                <Badge className={`${RSVP_STYLES[miStatus].bgLight} ${RSVP_STYLES[miStatus].textColor} border-0 text-[10px]`}>
                  {RSVP_STYLES[miStatus].icon} {RSVP_STYLES[miStatus].label}
                </Badge>
              )}
            </div>
            {fecha && (
              <p className="text-xs text-gray-500 mt-0.5">
                📅 {fecha.toLocaleDateString('es-UY', { timeZone: 'America/Montevideo' })} · {fecha.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })} · ⏱ {reunion.duracion_minutos || 60} min
              </p>
            )}
            <p className="text-xs text-gray-400 mt-0.5">Organiza: {reunion.organizador_nombre} · {inviCount} invitados</p>
            {reunion.descripcion && (
              <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{reunion.descripcion}</p>
            )}

            {/* Resumen y avatares de invitados con su RSVP */}
            {invitadosConRsvp.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-3 flex-wrap text-xs mb-2">
                  <span className="font-semibold text-gray-700">Asistencia:</span>
                  {resumen.aceptado > 0 && (
                    <span className="flex items-center gap-1 text-emerald-700"><span className="w-2 h-2 rounded-full bg-emerald-500" />{resumen.aceptado} asistirá{resumen.aceptado > 1 ? 'n' : ''}</span>
                  )}
                  {resumen.tal_vez > 0 && (
                    <span className="flex items-center gap-1 text-amber-700"><span className="w-2 h-2 rounded-full bg-amber-500" />{resumen.tal_vez} tal vez</span>
                  )}
                  {resumen.rechazado > 0 && (
                    <span className="flex items-center gap-1 text-red-700"><span className="w-2 h-2 rounded-full bg-red-500" />{resumen.rechazado} no podrá{resumen.rechazado > 1 ? 'n' : ''}</span>
                  )}
                  {resumen.pendiente > 0 && (
                    <span className="flex items-center gap-1 text-gray-500"><span className="w-2 h-2 rounded-full bg-gray-400" />{resumen.pendiente} sin responder</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {invitadosConRsvp.map((inv) => (
                    <InvitadoAvatar
                      key={inv.id}
                      invitado={inv}
                      canRecordar={canRecordar}
                      onClickAvatar={onAvatarClick}
                      ultimoRecordatorio={ultimoRecPorUsuario[inv.id]}
                    />
                  ))}
                  {canRecordar && resumen.pendiente > 0 && (
                    <button
                      type="button"
                      onClick={() => enviarRecordatorio('todos_pendientes')}
                      disabled={recordando}
                      className="ml-auto text-xs px-2.5 py-1.5 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300 font-semibold transition-all disabled:opacity-50 flex items-center gap-1"
                      title="Enviar email + push a todos los que aún no respondieron"
                      data-testid={`btn-recordar-pendientes-${reunion.id}`}
                    >
                      {recordando ? (
                        <><i className="fas fa-spinner fa-spin"></i>Enviando…</>
                      ) : (
                        <><i className="fas fa-bell"></i>Recordar a {resumen.pendiente} pendiente{resumen.pendiente > 1 ? 's' : ''}</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Botones RSVP para el invitado */}
            {puedoRsvp && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  {miStatus === 'pendiente' ? '¿Vas a asistir?' : 'Cambiar mi respuesta:'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => respond('aceptado')}
                    disabled={rsvpSubmitting}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition-all ${
                      miStatus === 'aceptado'
                        ? 'bg-emerald-600 text-white shadow-md'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                    }`}
                    data-testid={`rsvp-accept-${reunion.id}`}
                  >
                    ✅ Acepto
                  </button>
                  <button
                    onClick={() => respond('tal_vez')}
                    disabled={rsvpSubmitting}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition-all ${
                      miStatus === 'tal_vez'
                        ? 'bg-amber-500 text-white shadow-md'
                        : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                    }`}
                    data-testid={`rsvp-maybe-${reunion.id}`}
                  >
                    🤔 Tal vez
                  </button>
                  <button
                    onClick={() => respond('rechazado')}
                    disabled={rsvpSubmitting}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1 transition-all ${
                      miStatus === 'rechazado'
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                    }`}
                    data-testid={`rsvp-reject-${reunion.id}`}
                  >
                    ❌ No acepto
                  </button>
                </div>
                {showRejectReason && (
                  <div className="mt-2 space-y-2 bg-red-50 border border-red-200 rounded-md p-2">
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Razón (opcional)..."
                      rows={2}
                      maxLength={500}
                      className="w-full text-xs border border-red-200 rounded p-1.5 focus:outline-none focus:border-red-400 resize-none"
                      data-testid={`rsvp-reject-reason-${reunion.id}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => { setShowRejectReason(false); setRejectReason(''); }}
                        className="text-xs text-gray-600 hover:text-gray-800"
                        disabled={rsvpSubmitting}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => respond('rechazado')}
                        disabled={rsvpSubmitting}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-semibold"
                        data-testid={`rsvp-reject-confirm-${reunion.id}`}
                      >
                        {rsvpSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirmar rechazo'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {!isPast && reunion.estado === 'activa' && miStatus !== 'rechazado' && (
              <Button
                onClick={onJoin}
                disabled={joining}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`join-reunion-${reunion.id}`}
              >
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4 mr-1" />}
                Entrar
              </Button>
            )}
            {puedeModificar && (
              <>
                <Button
                  onClick={onEdit}
                  size="sm"
                  variant="outline"
                  className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                  data-testid={`edit-reunion-${reunion.id}`}
                >
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Editar
                </Button>
                {/* Botón "Copiar link de invitado externo" — permite invitar
                    a personas sin cuenta en IBA Connect (Daily.co acepta
                    a cualquiera con el URL público, solo pide el nombre). */}
                {!isPast && reunion.estado === 'activa' && reunion.daily_room_url && (
                  <Button
                    onClick={async () => {
                      // 1) Renovar la sala en Daily.co (asegura que no expire)
                      // antes de copiar el link, así garantizamos que funcione
                      // aunque la sala se haya creado hace meses con TTL viejo.
                      // La URL NO cambia.
                      let urlFinal = reunion.daily_room_url;
                      try {
                        const r = await axios.post(`${API}/sala-docente/reuniones/${reunion.id}/renovar-link`, {}, { headers: headersRef.current });
                        if (r?.data?.url) urlFinal = r.data.url;
                      } catch (_e) {
                        // Si falla, igual seguimos con el URL original (puede
                        // estar OK si la sala todavía no expiró).
                      }
                      const fecha = reunion.fecha_hora_inicio ? new Date(reunion.fecha_hora_inicio) : null;
                      const fechaStr = fecha ? `${fecha.toLocaleDateString('es-UY', { weekday: 'long', day: '2-digit', month: 'long', timeZone: 'America/Montevideo' })} a las ${fecha.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })} (hora Uruguay)` : '';
                      const texto = `Estás invitado a la reunión de Sala Docente:\n\n📌 ${reunion.titulo}\n${fechaStr ? `📅 ${fechaStr}\n` : ''}\n🔗 Unirse: ${urlFinal}\n\nNo necesitás cuenta en IBA Connect. Solo abrí el link y poné tu nombre al entrar.`;
                      try {
                        if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
                          await navigator.share({ title: `Reunión: ${reunion.titulo}`, text: texto });
                          return;
                        }
                        await navigator.clipboard.writeText(texto);
                        toast.success('Link de invitado copiado y renovado. Pegalo en WhatsApp, email o donde quieras.');
                      } catch (_e) {
                        window.prompt('Copiá el siguiente texto para invitar:', texto);
                      }
                    }}
                    size="sm"
                    variant="outline"
                    className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    title="Copiar link para invitar a alguien que no tiene cuenta en IBA Connect"
                    data-testid={`compartir-link-reunion-${reunion.id}`}
                  >
                    <i className="fas fa-share-alt mr-1.5" /> Invitar externo
                  </Button>
                )}
                <Button
                  onClick={onCancel}
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`cancel-reunion-${reunion.id}`}
                >
                  Cancelar
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CreateReunionModal = ({ isOpen, onClose, onCreated, API, profesores, currentUserId, editing }) => {
  // Convierte una Date a "YYYY-MM-DDTHH:mm" representando hora de URUGUAY,
  // sin importar la zona horaria del navegador del usuario.
  const toMontevideoLocalInput = (d) => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Montevideo',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(d);
      const get = (t) => parts.find(p => p.type === t)?.value || '00';
      return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    } catch (_e) {
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    }
  };
  const initialFecha = useMemo(() => {
    if (editing?.fecha_hora_inicio) {
      try {
        return toMontevideoLocalInput(new Date(editing.fecha_hora_inicio));
      } catch (_) {}
    }
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    return toMontevideoLocalInput(d);
  }, [editing]);

  const [titulo, setTitulo] = useState(editing?.titulo || '');
  const [descripcion, setDescripcion] = useState(editing?.descripcion || '');
  const [fecha, setFecha] = useState(initialFecha);
  const [duracion, setDuracion] = useState(editing?.duracion_minutos || 60);
  const [invitados, setInvitados] = useState(new Set(editing?.invitados_ids || []));
  const [invitarTodos, setInvitarTodos] = useState(!editing); // editing → mantener lista existente
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [rolFiltro, setRolFiltro] = useState('todos'); // todos | profesor | estudiante | admin
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const isEdit = !!editing;

  // Estilos de badges por rol (mismo patrón que en Citas)
  const rolChip = (rol) => {
    const r = (rol || '').toLowerCase();
    if (r === 'profesor')   return 'bg-blue-100 text-blue-700 border-blue-300';
    if (r === 'estudiante') return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (r === 'admin')      return 'bg-purple-100 text-purple-700 border-purple-300';
    if (r === 'pastor')     return 'bg-amber-100 text-amber-700 border-amber-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const conteoPorRol = useMemo(() => {
    const acc = { profesor: 0, estudiante: 0, admin: 0 };
    profesores.forEach((p) => { if (p.id !== currentUserId) acc[p.rol] = (acc[p.rol] || 0) + 1; });
    return acc;
  }, [profesores, currentUserId]);

  const profesoresFiltrados = useMemo(() => {
    const q = search.toLowerCase().trim();
    return profesores
      .filter(p => p.id !== currentUserId)
      .filter(p => rolFiltro === 'todos' || (p.rol || '').toLowerCase() === rolFiltro)
      .filter(p => !q || (p.nombre || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q));
  }, [profesores, currentUserId, search, rolFiltro]);

  const toggle = (id) => {
    setInvitados(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const submit = async (notify = true) => {
    if (titulo.trim().length < 3) { toast.error('Título muy corto'); return; }
    if (!fecha) { toast.error('Falta fecha/hora'); return; }
    setSubmitting(true);
    try {
      const body = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
        // Enviar la hora datetime-local TAL CUAL (sin conversión a UTC).
        // El backend la interpreta como hora de Uruguay (America/Montevideo)
        // y la convierte a UTC para almacenar.
        fecha_hora_inicio: fecha,
        duracion_minutos: Number(duracion) || 60,
        invitados: invitarTodos ? [] : Array.from(invitados),
        // Zona horaria fija: TODA la app muestra hora de Uruguay (Montevideo)
        // sin importar dónde esté el usuario. El backend formatea las
        // notificaciones (instantánea y recordatorio 15 min) en esta TZ.
        organizador_tz: 'America/Montevideo',
      };
      const qs = notify ? '' : '?notify=false';
      if (isEdit) {
        await axios.patch(`${API}/area-profesores/reuniones/${editing.id}${qs}`, body, { headers: headersRef.current });
        toast.success(notify ? 'Reunión actualizada y notificación enviada' : 'Reunión guardada (sin notificar a los invitados)');
      } else {
        await axios.post(`${API}/area-profesores/reuniones${qs}`, body, { headers: headersRef.current });
        toast.success(notify ? 'Reunión creada y notificación enviada' : 'Reunión creada (sin notificar a los invitados)');
      }
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar reunión');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-reunion-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Video className="w-5 h-5 text-indigo-600" /> {isEdit ? 'Editar Reunión' : 'Nueva Reunión Docente'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Título *</label>
            <input
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              maxLength={140}
              placeholder="Reunión de planificación semestral"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              data-testid="reunion-titulo"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Fecha y hora *</label>
              <input
                type="datetime-local"
                value={fecha}
                onChange={e => setFecha(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                data-testid="reunion-fecha"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Duración (minutos)</label>
              <select
                value={duracion}
                onChange={e => setDuracion(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>60 min</option>
                <option value={90}>1h 30 min</option>
                <option value={120}>2 horas</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Descripción (opcional)</label>
            <textarea
              value={descripcion}
              onChange={e => setDescripcion(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Agenda, link de material, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={invitarTodos}
                onChange={e => setInvitarTodos(e.target.checked)}
                className="w-4 h-4 accent-indigo-600"
              />
              <span className="font-medium text-gray-700">Invitar a todos los profesores y administradores</span>
            </label>
            <p className="text-[11px] text-gray-400 ml-6 mt-0.5">
              Esto NO invita estudiantes — para incluir alumnos, desmarcá y elegilos abajo.
            </p>
            {!invitarTodos && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
                <div className="sticky top-0 bg-white px-3 py-2 border-b space-y-2 z-10">
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar por nombre o email..."
                    className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                  {/* Chips filtro por rol */}
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      { key: 'todos',      label: `Todos`,        count: profesores.length - (profesores.some(p => p.id === currentUserId) ? 1 : 0), cls: 'bg-gray-100 text-gray-700 border-gray-300' },
                      { key: 'profesor',   label: `Profesores`,   count: conteoPorRol.profesor   || 0, cls: 'bg-blue-100 text-blue-700 border-blue-300' },
                      { key: 'estudiante', label: `Estudiantes`,  count: conteoPorRol.estudiante || 0, cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
                      { key: 'admin',      label: `Admins`,       count: conteoPorRol.admin      || 0, cls: 'bg-purple-100 text-purple-700 border-purple-300' },
                    ].filter(c => c.key === 'todos' || c.count > 0).map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setRolFiltro(c.key)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition ${rolFiltro === c.key ? `${c.cls} ring-2 ring-offset-1 ring-indigo-400` : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                        data-testid={`sd-reunion-filtro-${c.key}`}
                      >
                        {c.label} ({c.count})
                      </button>
                    ))}
                  </div>
                </div>
                {profesoresFiltrados.length === 0 && (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">Sin resultados</p>
                )}
                {profesoresFiltrados.map(p => (
                  <label key={p.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0">
                    <input
                      type="checkbox"
                      checked={invitados.has(p.id)}
                      onChange={() => toggle(p.id)}
                      className="w-4 h-4 accent-indigo-600"
                    />
                    {p.image ? (
                      <img src={p.image} alt={p.nombre} className="w-7 h-7 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0">
                        {(p.nombre || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate flex items-center gap-1.5">
                        <span className="truncate">{p.nombre}</span>
                        <span className={`text-[9px] uppercase font-bold px-1 py-0 rounded border ${rolChip(p.rol)}`}>{p.rol}</span>
                      </p>
                      <p className="text-xs text-gray-400 truncate">{p.email}</p>
                    </div>
                  </label>
                ))}
                <p className="sticky bottom-0 px-3 py-2 text-xs text-gray-500 border-t bg-gray-50">
                  {invitados.size} seleccionado(s)
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex flex-wrap justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancelar
          </button>
          <Button
            onClick={() => submit(false)}
            disabled={submitting || titulo.trim().length < 3}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
            title="Guarda los cambios sin enviar emails ni push notifications. Útil para correcciones menores."
            data-testid="reunion-save-silent-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <i className="fas fa-floppy-disk mr-1.5" />}
            Guardar (sin notificar)
          </Button>
          <Button
            onClick={() => submit(true)}
            disabled={submitting || titulo.trim().length < 3}
            className="bg-indigo-600 hover:bg-indigo-700"
            title="Guarda y envía email + push a los invitados con la nueva información."
            data-testid="reunion-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <i className="fas fa-bell mr-1.5" />}
            {isEdit ? 'Guardar y notificar' : 'Crear y notificar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Calendario Tab — Vista de calendario mensual + panel de eventos del día
// ============================================================================
const CalendarioTab = ({ API, user }) => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/area-profesores/calendario`, { headers: headersRef.current });
        setEventos(r.data.eventos || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [API]);

  // Identificar evento propio: backend ya envía `es_mia` para clases/reuniones.
  // Fallback: comparar email/id por si algún evento legacy no trae la bandera.
  const myEmail = (user?.email || '').toLowerCase();
  const myId = user?.id;
  const isMine = useCallback((e) => {
    if (e?.es_mia === true) return true;
    if (e?.soy_organizador === true) return true;
    if (e?.profesor_email && myEmail && e.profesor_email.toLowerCase() === myEmail) return true;
    if (e?.profesor_id && myId && e.profesor_id === myId) return true;
    return false;
  }, [myEmail, myId]);

  // Helper: convertir Date a "YYYY-MM-DD" en tiempo LOCAL (no UTC).
  // Usar toISOString().slice(0,10) es incorrecto para eventos a las 21:00 UY,
  // porque en UTC caen en el día siguiente y aparecerían mal en el calendario.
  const localYMD = useCallback((d) => {
    if (!d || isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, []);

  // Map: día (YYYY-MM-DD LOCAL) → eventos
  const eventosPorDia = useMemo(() => {
    const map = new Map();
    eventos.forEach(e => {
      if (!e.fecha_hora_inicio) return;
      const d = new Date(e.fecha_hora_inicio);
      if (isNaN(d.getTime())) return;
      const k = localYMD(d);
      const arr = map.get(k) || [];
      arr.push(e);
      map.set(k, arr);
    });
    return map;
  }, [eventos, localYMD]);

  // Días que sólo contienen eventos propios vs sólo ajenos vs mixtos
  const dayStatus = useCallback((date) => {
    if (!date) return null;
    const k = localYMD(date);
    const arr = eventosPorDia.get(k);
    if (!arr || arr.length === 0) return null;
    const mine = arr.some(isMine);
    const other = arr.some(e => !isMine(e));
    if (mine && other) return 'mixto';
    return mine ? 'mias' : 'otras';
  }, [eventosPorDia, isMine, localYMD]);

  const eventosDelDia = useMemo(() => {
    if (!selectedDate) return [];
    const k = localYMD(selectedDate);
    return (eventosPorDia.get(k) || []).sort((a, b) =>
      (a.fecha_hora_inicio || '').localeCompare(b.fecha_hora_inicio || '')
    );
  }, [selectedDate, eventosPorDia, localYMD]);

  const formatDateHeader = (d) =>
    d ? format(d, "EEEE d 'de' MMMM", { locale: es }) : '';

  if (loading) {
    return (
      <div className="p-10 flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2">
          <Card className="shadow-sm border-gray-100">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-lg">
                <Calendar className="w-5 h-5 text-indigo-600 mr-2" />
                {format(selectedDate || new Date(), "MMMM yyyy", { locale: es })
                  .replace(/^./, (c) => c.toUpperCase())}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CalendarUI
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                locale={es}
                className="rounded-md border shadow-sm"
                modifiers={{
                  dayMine: (date) => dayStatus(date) === 'mias',
                  dayOther: (date) => dayStatus(date) === 'otras',
                  dayMixed: (date) => dayStatus(date) === 'mixto',
                }}
                modifiersStyles={{
                  dayMine: {
                    backgroundColor: '#dbeafe',
                    color: '#1e40af',
                    fontWeight: 'bold',
                  },
                  dayOther: {
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    fontWeight: 'bold',
                  },
                  dayMixed: {
                    background: 'linear-gradient(135deg, #dbeafe 0%, #dbeafe 50%, #f3f4f6 50%, #f3f4f6 100%)',
                    color: '#1e3a8a',
                    fontWeight: 'bold',
                  },
                }}
                data-testid="sala-docente-calendar"
              />
              {/* Legenda */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 text-sm mb-2">Referencias</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border border-blue-300" style={{ backgroundColor: '#dbeafe' }}></span>
                    <span className="text-gray-700">Mis clases / reuniones</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block w-4 h-4 rounded border border-gray-300" style={{ backgroundColor: '#f3f4f6' }}></span>
                    <span className="text-gray-700">Otras clases / eventos</span>
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <span
                      className="inline-block w-4 h-4 rounded border border-gray-300"
                      style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #dbeafe 50%, #f3f4f6 50%, #f3f4f6 100%)' }}
                    ></span>
                    <span className="text-gray-700">Día mixto (mías + otras)</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Panel eventos del día */}
        <div className="space-y-4">
          <Card className="shadow-sm border-gray-100">
            <CardHeader className="pb-2 bg-gradient-to-r from-indigo-50 to-blue-50">
              <CardTitle className="text-base">
                {formatDateHeader(selectedDate).replace(/^./, (c) => c.toUpperCase())}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {eventosDelDia.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">
                  No hay eventos en este día.
                </p>
              ) : (
                <div className="space-y-2">
                  {eventosDelDia.map((e) => {
                    const fecha = e.fecha_hora_inicio ? new Date(e.fecha_hora_inicio) : null;
                    const mine = isMine(e);
                    const itemClasses = mine
                      ? 'bg-blue-50 border-blue-200 hover:border-blue-400'
                      : 'bg-white border-gray-200 hover:border-gray-400';
                    return (
                      <button
                        key={e.id}
                        onClick={() => e.url && navigate(e.url)}
                        className={`w-full text-left border hover:shadow-sm rounded-lg p-3 transition-all flex items-start gap-3 ${itemClasses} ${e.rsvp_status === 'tal_vez' ? 'border-dashed' : ''}`}
                        data-testid={`evento-${e.id}`}
                      >
                        <div className="text-2xl shrink-0">{e.icon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-gray-800 text-sm truncate">{e.titulo}</p>
                            {mine && (
                              <Badge className="bg-blue-600 hover:bg-blue-600 text-white text-[10px] px-1.5 py-0">
                                Mía
                              </Badge>
                            )}
                            {e.rsvp_status === 'aceptado' && (
                              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px] px-1.5 py-0">
                                ✓ Asistiré
                              </Badge>
                            )}
                            {e.rsvp_status === 'tal_vez' && (
                              <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] px-1.5 py-0">
                                ? Tentativa
                              </Badge>
                            )}
                          </div>
                          {fecha && (
                            <p className="text-xs text-gray-500">
                              🕐 {fecha.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })}
                              {e.duracion_minutos && ` · ${e.duracion_minutos} min`}
                            </p>
                          )}
                          {e.profesor && (
                            <p className="text-xs text-gray-400 truncate">Prof. {e.profesor}</p>
                          )}
                          {e.organizador && (
                            <p className="text-xs text-gray-400 truncate">{e.organizador}</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Helpers de videollamada en DMs de Sala Docente
// ============================================================================

// Optimización para mobile: prioriza vista de active speaker en pantallas chicas
const optimizeDailyUrlForMobile = (url) => {
  if (!url || typeof window === 'undefined') return url;
  const isMobile = window.innerWidth < 768;
  if (!isMobile) return url;
  try {
    const u = new URL(url);
    if (!u.searchParams.has('showLocalVideo')) u.searchParams.set('showLocalVideo', 'true');
    if (!u.searchParams.has('showParticipantsBar')) u.searchParams.set('showParticipantsBar', 'false');
    if (!u.searchParams.has('showFullscreenButton')) u.searchParams.set('showFullscreenButton', 'false');
    if (!u.searchParams.has('layout')) u.searchParams.set('layout', 'active-speaker');
    return u.toString();
  } catch (_e) {
    return url;
  }
};

// Genera URL de Daily.co con token personal del usuario (para no pedir el nombre)
const buildSDCallUrl = async (url) => {
  if (!url) return url;
  try {
    const token = localStorage.getItem('token');
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    const resp = await fetch(`${origin}/api/chat/video-join-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ room_url: url }),
    });
    if (!resp.ok) return optimizeDailyUrlForMobile(url);
    const data = await resp.json();
    return optimizeDailyUrlForMobile(data.url || url);
  } catch (_e) {
    return optimizeDailyUrlForMobile(url);
  }
};

// Custom message: renderiza una card especial para los mensajes con `video_call: true`
// (y un mini-aviso compacto cuando es `video_call_ended`).
// Helper: extrae el ID de un video de YouTube desde una URL en cualquier formato.
// Soporta: youtu.be/xxx, youtube.com/watch?v=xxx, youtube.com/shorts/xxx, youtube.com/embed/xxx.
const extractYoutubeId = (text) => {
  if (!text || typeof text !== 'string') return null;
  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{6,})/i,
    /youtube\.com\/watch\?[^\s]*v=([A-Za-z0-9_-]{6,})/i,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/i,
    /youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/i,
    /youtube\.com\/live\/([A-Za-z0-9_-]{6,})/i,
  ];
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m[1]) return m[1].split(/[?&#]/)[0];
  }
  return null;
};

const SDCustomMessage = (props) => {
  const { message } = useMessageContext();
  const { client } = useChatContext();
  if (message?.video_call_ended) {
    return (
      <div className="flex justify-center my-2">
        <div className="px-3 py-1 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center gap-1.5">
          <i className="fas fa-phone-slash" />
          <span>Llamada finalizada</span>
        </div>
      </div>
    );
  }
  if (message?.video_call && message?.video_call_url) {
    const ownMsg = message.user?.id === client?.userID;
    const caller = message.video_call_caller || message.user?.name || 'Alguien';
    const url = message.video_call_url;
    const userName = client?.user?.name || message.user?.name || 'Invitado';
    const startedAt = message.video_call_started_at || (message.created_at ? new Date(message.created_at).getTime() : 0);
    const ageMin = startedAt ? Math.floor((Date.now() - startedAt) / 60000) : null;
    return (
      <div className={`str-chat__message str-chat__message-simple ${ownMsg ? 'str-chat__message--me' : ''} my-2`} data-testid="sd-video-call-message">
        <div className={`flex ${ownMsg ? 'justify-end' : 'justify-start'} w-full`}>
          <div className="rounded-2xl overflow-hidden shadow-lg max-w-xs bg-gradient-to-br from-emerald-500 via-green-500 to-teal-500">
            <div className="p-4 text-white">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                  <Video className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-base truncate">Videollamada</p>
                  <p className="text-xs opacity-90 truncate">
                    {ownMsg ? 'Iniciaste una llamada' : `${caller} llamó`}
                    {ageMin != null && ageMin >= 0 && (
                      <> · {ageMin === 0 ? 'ahora' : `hace ${ageMin} min`}</>
                    )}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('iba:open-video-call', {
                    detail: { url, channelId: message.cid, userName, source: 'sala_docente' },
                  }));
                }}
                className="w-full py-2.5 bg-white text-green-700 rounded-xl font-bold text-sm hover:bg-green-50 transition-all active:scale-95 shadow-md"
                data-testid="sd-join-call-btn"
              >
                <i className="fas fa-video mr-2" />Unirse a la llamada
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // YouTube embed: si el mensaje contiene un link de YouTube, mostramos el player debajo
  const ytId = extractYoutubeId(message?.text || '');
  if (ytId) {
    const ownMsg = message.user?.id === client?.userID;
    return (
      <div className="flex flex-col">
        <MessageSimple {...props} />
        <div className={`flex ${ownMsg ? 'justify-end' : 'justify-start'} px-2 -mt-1 mb-2`}>
          <div
            className="rounded-xl overflow-hidden shadow-md border border-gray-200 bg-black"
            style={{ maxWidth: '420px', width: '100%' }}
            data-testid="sd-youtube-embed"
          >
            <div className="relative" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={`https://www.youtube.com/embed/${ytId}?rel=0`}
                title="YouTube video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <MessageSimple {...props} />;
};

// =========================================================================
// Online Presence + OOO Context
// Provee:
//   - getStatus(userId): 'online' | 'ooo' | null
// El estado online viene de Stream Chat presence (event user.presence.changed).
// El estado OOO viene del backend (/api/stream-chat/ooo-list), refrescado 60s.
// =========================================================================
const OOOContext = React.createContext(new Set());

const OOOProvider = ({ children, API, headersRef }) => {
  const [oooSet, setOooSet] = useState(new Set());
  useEffect(() => {
    let cancel = false;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${API}/stream-chat/ooo-list`, { headers: headersRef?.current || {} });
        if (cancel) return;
        const ids = (res.data?.ooo_user_ids) || [];
        setOooSet(new Set(ids));
      } catch (_e) {
        // silencioso — si falla, simplemente no mostramos dot rojo
      }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, 60000); // refresh cada 60s
    return () => { cancel = true; if (timer) clearInterval(timer); };
  }, [API, headersRef]);
  return <OOOContext.Provider value={oooSet}>{children}</OOOContext.Provider>;
};

// Header personalizado: muestra título del DM y un botón de videollamada
// SÓLO cuando el canal es un DM de Sala Docente (id empieza con `sdm-`).
const SDChannelHeader = ({ onBackToList, showBack }) => {
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();
  const channelId = channel?.id || '';
  const isDM = channelId.startsWith('sdm-') || channel?.data?.sala_docente_dm === true;
  const [starting, setStarting] = useState(false);

  const startVideoCall = async () => {
    if (!channel?.id || starting) return;
    setStarting(true);
    const toastId = toast.loading('Creando sala de videollamada...');
    try {
      const token = localStorage.getItem('token');
      const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
      const membersMap = channel?.state?.members || {};
      const notifyUserIds = Object.values(membersMap)
        .map((m) => m?.user_id || m?.user?.id)
        .filter((uid) => uid && uid !== client?.userID);

      const resp = await fetch(`${origin}/api/chat/video-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          channel_id: channel.id,
          channel_name: channel?.data?.name || '',
          is_team: false,
          notify_user_ids: notifyUserIds,
        }),
      });
      if (!resp.ok) throw new Error('No se pudo crear la sala');
      const data = await resp.json();
      toast.dismiss(toastId);
      const callerName = client?.user?.name || 'Alguien';

      // Mensaje al chat con metadata para que el receptor reciba el popup global
      try {
        await channel.sendMessage({
          text: `📹 ${callerName} inició una videollamada. Tocá para unirte: ${data.url}`,
          video_call: true,
          video_call_url: data.url,
          video_call_caller: callerName,
          video_call_started_at: Date.now(),
        });
      } catch (_e) { /* no bloquear si falla el envío del mensaje */ }

      // Abrir iframe local para el caller via evento global
      const finalUrl = await buildSDCallUrl(data.url);
      window.dispatchEvent(new CustomEvent('iba:open-video-call', {
        detail: { url: finalUrl, channelId: channel.cid || channel.id, userName: callerName, source: 'sala_docente' },
      }));
    } catch (e) {
      toast.dismiss(toastId);
      console.error('Error iniciando videollamada Sala Docente:', e);
      toast.error('No se pudo iniciar la videollamada. Intentá de nuevo.');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="relative">
      {showBack && (
        <button
          onClick={onBackToList}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-full p-2 shadow-sm hover:bg-gray-100 transition-colors lg:hidden"
          title="Volver a la lista"
          data-testid="sd-back-to-list-btn"
        >
          <ArrowLeft className="w-4 h-4 text-gray-700" />
        </button>
      )}
      <ChannelHeader />
      {isDM && (
        <button
          onClick={startVideoCall}
          disabled={starting}
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full p-2 shadow-md disabled:opacity-50 transition-all"
          title="Iniciar videollamada"
          data-testid="sd-start-video-call-btn"
        >
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
};

// Input personalizado del chat: agrega botón de encuesta nativa de Stream Chat
const SDMessageInput = () => {
  const { channel } = useChannelStateContext();
  const { client } = useChatContext();
  const [showPollModal, setShowPollModal] = useState(false);

  const handleCreatePoll = async ({ question, options }) => {
    try {
      const poll = await client.createPoll({
        name: question,
        description: '',
        options: options.map((text) => ({ text })),
        enforce_unique_vote: true,
        max_votes_allowed: 1,
        allow_user_suggested_options: false,
        voting_visibility: 'public',
      });
      await channel.sendMessage({
        text: `📊 ${question}`,
        poll_id: poll.poll.id,
      });
      toast.success('📊 Encuesta publicada');
    } catch (e) {
      console.error('Error creando poll en chat:', e);
      toast.error('No se pudo crear la encuesta');
    }
  };

  return (
    <div className="flex items-center gap-1 w-full">
      <div className="flex-1">
        {/* audioRecordingEnabled habilita el botón de grabar audio (icono mic) */}
        <MessageInput focus grow maxRows={10} audioRecordingEnabled />
      </div>
      <button
        type="button"
        onClick={() => setShowPollModal(true)}
        className="p-2 rounded-full hover:bg-purple-50 text-purple-600 transition-colors shrink-0"
        title="Crear encuesta"
        data-testid="sd-create-poll-btn"
      >
        <BarChart3 className="w-5 h-5" />
      </button>
      {showPollModal && (
        <SDPollModal
          isOpen={showPollModal}
          onClose={() => setShowPollModal(false)}
          onSubmit={handleCreatePoll}
        />
      )}
    </div>
  );
};

// Modal simple para crear encuesta en el chat
const SDPollModal = ({ isOpen, onClose, onSubmit }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const updateOpt = (i, v) => setOptions((p) => p.map((o, idx) => (idx === i ? v : o)));
  const addOpt = () => { if (options.length < 6) setOptions((p) => [...p, '']); };
  const removeOpt = (i) => { if (options.length > 2) setOptions((p) => p.filter((_, idx) => idx !== i)); };

  const submit = async () => {
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (question.trim().length < 3) { toast.error('La pregunta es muy corta'); return; }
    if (clean.length < 2) { toast.error('Necesitás al menos 2 opciones'); return; }
    setSubmitting(true);
    try {
      await onSubmit({ question: question.trim(), options: clean });
      setQuestion(''); setOptions(['', '']);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="sd-poll-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">📊 Nueva Encuesta</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Pregunta..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
            maxLength={200}
            data-testid="sd-poll-question"
          />
          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={opt}
                  onChange={(e) => updateOpt(i, e.target.value)}
                  placeholder={`Opción ${i + 1}`}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
                  maxLength={100}
                />
                {options.length > 2 && (
                  <button onClick={() => removeOpt(i)} className="text-gray-400 hover:text-red-500 p-1">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {options.length < 6 && (
              <button onClick={addOpt} className="text-sm text-purple-600 hover:text-purple-700 font-medium">
                + Agregar opción
              </button>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">
              Cancelar
            </button>
            <button
              onClick={submit}
              disabled={submitting || !question.trim()}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg text-sm font-semibold"
              data-testid="sd-poll-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Publicar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Channel Preview personalizado: dispara `onMobileSelect` cuando se elige un canal
// para que el layout mobile pueda deslizar a la vista de chat.
const SDChannelPreview = (props) => {
  const { channel, setActiveChannel, activeChannel, displayTitle, latestMessage, unread, onMobileSelect } = props;
  const { client } = useChatContext();
  const isActive = activeChannel?.cid === channel?.cid;
  const channelName = channel?.data?.name || displayTitle || channel?.id || '';
  const lastMsg = typeof latestMessage === 'string' ? latestMessage : (latestMessage?.props?.children || 'Sin mensajes');

  // Para DMs, mostrar nombre + avatar del otro miembro
  let avatarImage = channel?.data?.image || null;
  let display = channelName;
  let otherUserId = null;
  let otherIsOnline = false;
  const channelId = channel?.id || '';
  const isDM = channelId.startsWith('sdm-') || channel?.data?.sala_docente_dm === true;
  if (isDM) {
    const members = Object.values(channel?.state?.members || {});
    const other = members.find((m) => m.user_id !== client?.userID);
    avatarImage = other?.user?.image || avatarImage;
    display = other?.user?.name || channelName;
    otherUserId = other?.user_id || null;
    // Stream Chat con presence:true expone user.online (boolean)
    otherIsOnline = !!other?.user?.online;
  }

  // Estado de OOO (rojo) consumido del contexto global
  const oooSet = useContext(OOOContext);
  const isOOO = otherUserId && oooSet.has(otherUserId);
  // Re-render cuando cambia presencia o se actualiza la lista OOO
  const [, _force] = useState(0);
  useEffect(() => {
    if (!client || !otherUserId) return;
    const onPresence = (ev) => {
      if (ev?.user?.id === otherUserId) _force((x) => x + 1);
    };
    client.on('user.presence.changed', onPresence);
    return () => { try { client.off('user.presence.changed', onPresence); } catch (_e) {} };
  }, [client, otherUserId]);

  // Determinar color del dot: rojo (OOO) gana sobre verde (online)
  let dotColor = null;
  let dotTitle = '';
  if (isOOO) { dotColor = 'bg-red-500'; dotTitle = 'Fuera de oficina'; }
  else if (otherIsOnline) { dotColor = 'bg-green-500'; dotTitle = 'En línea'; }

  return (
    <button
      onClick={() => {
        setActiveChannel(channel);
        if (onMobileSelect) onMobileSelect();
      }}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-l-[3px] ${
        isActive ? 'bg-indigo-50 border-l-indigo-500' : 'hover:bg-gray-50 border-l-transparent'
      }`}
      data-testid={`sd-channel-preview-${channel?.id}`}
    >
      <div className="relative shrink-0">
        {avatarImage ? (
          <img src={avatarImage} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-indigo-500 text-white font-semibold flex items-center justify-center text-sm">
            {isDM ? display.charAt(0).toUpperCase() : '#'}
          </div>
        )}
        {isDM && dotColor && (
          <span
            title={dotTitle}
            data-testid={`sd-presence-dot-${otherUserId}-${isOOO ? 'ooo' : 'online'}`}
            className={`absolute bottom-0 right-0 w-2.5 h-2.5 ${dotColor} border-2 border-white rounded-full`}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-sm truncate ${unread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>{display}</p>
        <p className="text-xs text-gray-500 truncate">{lastMsg || 'Sin mensajes'}</p>
      </div>
      {unread > 0 && (
        <span className="bg-indigo-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
          {unread}
        </span>
      )}
    </button>
  );
};

// ============================================================================
// Chat Tab — Embebido inline con canal grupal Sala Docente + DMs entre profesores
// ============================================================================
let _salaDocenteChatClient = null;
const ChatTab = ({ API }) => {
  const [client, setClient] = useState(null);
  const [myUserId, setMyUserId] = useState(null);
  const [error, setError] = useState(null);
  const [showNewDM, setShowNewDM] = useState(false);
  const [profesores, setProfesores] = useState([]);
  // Videollamada: estado global via VideoCallContext (vive en App.js para
  // sobrevivir a cambios de ruta, ej. abrir ficha de alumno desde Avance).
  const { openCall } = useVideoCall();
  // Mobile responsive: alternar entre lista de canales y conversación activa
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Asegurar canal grupal + cargar profesores en paralelo
        const [, profsRes, tkRes] = await Promise.all([
          axios.post(`${API}/area-profesores/chat/ensure-channel`, {}, { headers: headersRef.current }),
          axios.get(`${API}/area-profesores/profesores-disponibles`, { headers: headersRef.current }),
          axios.get(`${API}/stream-chat/token`, { headers: headersRef.current }),
        ]);
        if (cancelled) return;
        const { token, user_id, user_name, user_image, api_key } = tkRes.data;
        let c = _salaDocenteChatClient || StreamChat.getInstance(api_key);
        if (!c.userID) {
          await c.connectUser({ id: user_id, name: user_name, image: user_image || undefined }, token);
        }
        _salaDocenteChatClient = c;
        setProfesores(profsRes.data.profesores || []);
        setMyUserId(user_id);
        setClient(c);
      } catch (e) {
        console.error('Sala Docente chat init error:', e);
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'No se pudo cargar el chat');
      }
    })();
    return () => { cancelled = true; };
  }, [API]);

  // Filtro de canales: solo canales con scope=area_profesores donde el user es miembro
  // (incluye el canal grupal "sala-docente-iba" y los DMs entre profesores).
  const channelFilters = useMemo(() => {
    if (!myUserId) return null;
    return {
      type: 'messaging',
      scope: 'area_profesores',
      members: { $in: [myUserId] },
    };
  }, [myUserId]);

  const sort = useMemo(() => [{ last_message_at: -1 }], []);
  const options = useMemo(() => ({ state: true, watch: true, presence: true, limit: 30 }), []);

  // Listener para abrir el iframe de videollamada en Sala Docente (caller side
  // o cuando se toca "Unirse a la llamada" desde una card de mensaje).
  useEffect(() => {
    if (!client) return;
    const handler = async (ev) => {
      const { url, channelId, source } = ev.detail || {};
      if (!url) return;
      // Solo manejamos eventos originados en Sala Docente para no interferir con
      // el StreamChatComponent global (que maneja los del chat normal).
      // Si no hay `source`, asumimos que viene del CustomMessage card (legacy)
      // y lo aceptamos también si estamos en un canal de Sala Docente.
      if (source && source !== 'sala_docente') return;
      const finalUrl = await buildSDCallUrl(url);
      openCall({ url: finalUrl, channelId: channelId || null });
    };
    window.addEventListener('iba:open-video-call', handler);
    return () => window.removeEventListener('iba:open-video-call', handler);
  }, [client, openCall]);

  // Listener para enviar el mensaje "📵 Llamada finalizada" cuando el botón
  // de cerrar del iframe global (en VideoCallProvider) dispara el evento.
  // El send-message necesita el Stream chat client, que solo vive acá.
  useEffect(() => {
    if (!client) return;
    const handler = async (ev) => {
      try {
        const cid = ev.detail?.channelId;
        if (!cid) return;
        const [type, id] = String(cid).split(':');
        if (!type || !id) return;
        const ch = client.channel(type, id);
        await ch.watch();
        await ch.sendMessage({ text: '📵 Llamada finalizada', video_call_ended: true, silent: true });
      } catch (_e) { /* no-op */ }
    };
    window.addEventListener('iba:call-ended', handler);
    return () => window.removeEventListener('iba:call-ended', handler);
  }, [client]);

  // 🔔 Push notifications: disparar /api/stream-chat/notify cuando el user
  // envía un mensaje desde Sala Docente. Antes este trigger SOLO estaba en
  // StreamChatComponent (chat regular), por lo que los DMs de Sala Docente
  // no generaban push. Como el componente Sala Docente NO está montado al
  // mismo tiempo que el chat regular, registramos su propio listener acá.
  useEffect(() => {
    if (!client || !myUserId) return;
    const processed = new Set();
    const messageListener = async (event) => {
      try {
        const msgId = event.message?.id;
        if (!msgId || processed.has(msgId)) return;
        processed.add(msgId);
        if (processed.size > 200) {
          const arr = [...processed];
          arr.slice(0, 100).forEach((id) => processed.delete(id));
        }
        // Solo procesar si NOSOTROS somos el remitente
        if (event.user?.id !== myUserId) return;
        // Ignorar mensajes silenciosos (ej: "Llamada finalizada")
        if (event.message?.silent === true) return;

        const ch = client.channel(event.channel_type, event.channel_id);

        // Buscar todos los miembros del canal (DM o canal grupal de Sala Docente)
        let memberIds = [];
        try {
          const mr = await ch.queryMembers({});
          memberIds = (mr.members || [])
            .map((m) => m.user_id || m.user?.id)
            .filter((id) => id && id !== myUserId);
        } catch (qErr) {
          memberIds = Object.keys(ch.state?.members || {}).filter((id) => id !== myUserId);
        }
        if (memberIds.length === 0) return;

        const myName = client?.user?.name || '';
        const msgText =
          event.message?.text ||
          (event.message?.attachments?.length > 0 ? 'Envió un archivo' : '');

        // Fire-and-forget al backend (mismo endpoint que usa el chat regular)
        axios
          .post(
            `${API}/stream-chat/notify`,
            {
              recipient_ids: memberIds,
              sender_name: myName,
              message_text: msgText,
              channel_name: ch.data?.name || '',
              channel_type: event.channel_type || 'messaging',
              channel_id: event.channel_id || '',
            },
            { headers: headersRef.current }
          )
          .catch(() => {});
      } catch (e) {
        // No bloquear UI ante errores del listener
        console.warn('[Sala Docente] push notify listener error:', e);
      }
    };
    client.on('message.new', messageListener);
    return () => {
      try {
        client.off('message.new', messageListener);
      } catch (_e) {}
    };
  }, [client, myUserId, API]);

  // Cerrar videollamada: avisar al canal con "Llamada finalizada"
  // (endActiveCall fue removido — el cierre de la llamada ahora lo maneja
  // VideoCallProvider via closeCall(). SalaDocente solo escucha el evento
  // `iba:call-ended` para enviar el mensaje silencioso al canal.)

  const startDM = async (profEmail, profName) => {
    if (!client) return;
    const otherUserId = profEmail.replace(/@/g, '_at_').replace(/\./g, '_');
    if (otherUserId === myUserId) return;
    try {
      // Asegurar que el otro user exista en Stream
      // (lo hacemos via backend ensure-channel-dm)
      const r = await axios.post(
        `${API}/area-profesores/chat/ensure-dm`,
        { other_user_email: profEmail },
        { headers: headersRef.current }
      );
      const channelId = r.data.channel_id;
      // Abrir el canal directamente
      const dm = client.channel('messaging', channelId);
      await dm.watch();
      toast.success(`Chat con ${profName} abierto`);
      setShowNewDM(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo abrir el chat directo');
    }
  };

  if (error) {
    return <div className="p-6 text-center text-red-600"><p>{error}</p></div>;
  }
  if (!client || !channelFilters) {
    return (
      <div className="p-12 flex flex-col items-center justify-center gap-3 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        <p className="text-sm">Conectando al chat de Sala Docente...</p>
      </div>
    );
  }

  return (
    <div className="sala-docente-chat" style={{ height: isMobile ? 'calc(100vh - 240px)' : '70vh', minHeight: 480 }}>
      <style>{`
        .sala-docente-chat .str-chat { height: 100%; display: flex; flex-direction: column; }
        .sala-docente-chat .sd-chat-root { flex: 1 1 auto; min-height: 0; display: flex; width: 100%; }
        .sala-docente-chat .str-chat__channel-list { width: 100%; height: 100%; }
        .sala-docente-chat .str-chat__channel-list-messenger { height: 100%; }
        .sala-docente-chat .str-chat__main-panel { flex: 1; height: 100%; min-width: 0; }
        .sala-docente-chat .str-chat__channel { height: 100%; }
        .sala-docente-chat .str-chat__container { height: 100%; }
        .sala-docente-chat .str-chat__list { padding-bottom: 8px; }
        .sala-docente-chat .str-chat__message-input { padding: 8px 12px; }
        .sala-docente-chat .sd-mobile-hidden { display: none !important; }
        /* NOTA: el fix mobile del <Thread /> está en App.css para aplicar globalmente
           a todos los usos del SDK Stream Chat (chat general + Sala Docente). */
      `}</style>
      <Chat client={client} theme="messaging light">
        <OOOProvider API={API} headersRef={headersRef}>
        <div className="sd-chat-root flex h-full w-full overflow-hidden">
          {/* Lista de canales — ancho completo en mobile, fijo en desktop */}
          <div
            className={`flex flex-col bg-white ${
              isMobile
                ? `w-full ${mobileShowChat ? 'sd-mobile-hidden' : ''}`
                : 'border-r border-gray-200'
            }`}
            style={isMobile ? {} : { width: 280, flexShrink: 0 }}
          >
            <div className="p-2 border-b border-gray-100 bg-gray-50 shrink-0">
              <Button
                onClick={() => setShowNewDM(true)}
                size="sm"
                variant="outline"
                className="w-full text-xs"
                data-testid="new-dm-btn"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo chat directo
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto min-h-0">
              <ChannelList
                filters={channelFilters}
                sort={sort}
                options={options}
                Preview={(p) => (
                  <SDChannelPreview
                    {...p}
                    onMobileSelect={isMobile ? () => setMobileShowChat(true) : undefined}
                  />
                )}
              />
            </div>
          </div>

          {/* Panel de conversación — oculto en mobile cuando se está viendo la lista */}
          <div
            className={`flex-1 min-w-0 ${
              isMobile && !mobileShowChat ? 'sd-mobile-hidden' : ''
            }`}
          >
            <Channel Message={SDCustomMessage}>
              <Window>
                <SDChannelHeader
                  showBack={isMobile}
                  onBackToList={() => setMobileShowChat(false)}
                />
                <MessageList />
                <SDMessageInput />
              </Window>
              <Thread />
            </Channel>
          </div>
        </div>
        </OOOProvider>
      </Chat>

      {showNewDM && (
        <NewDMModal
          profesores={profesores.filter(p => {
            const pid = (p.email || '').replace(/@/g, '_at_').replace(/\./g, '_');
            return pid !== myUserId;
          })}
          onClose={() => setShowNewDM(false)}
          onSelect={(p) => startDM(p.email, p.nombre)}
        />
      )}

      {/* (Iframe de videollamada removido de acá — ahora vive a nivel App.js
          en VideoCallProvider para sobrevivir a navegaciones de ruta. Sala
          Docente solo dispara `openCall` cuando llega `iba:open-video-call`.) */}
    </div>
  );
};

const NewDMModal = ({ profesores, onClose, onSelect }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(
    () => profesores.filter(p => p.nombre.toLowerCase().includes(search.toLowerCase())),
    [profesores, search]
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Nuevo chat directo</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-3 border-b">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar profesor o administrador..."
            className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="new-dm-search"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="p-6 text-sm text-gray-400 text-center">Sin resultados</p>
          ) : (
            filtered.map(p => (
              <button
                key={p.id}
                onClick={() => onSelect(p)}
                className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50"
                data-testid={`dm-target-${p.id}`}
              >
                {p.image ? (
                  <img src={p.image} alt={p.nombre} className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-sm">
                    {p.nombre.slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{p.nombre}</p>
                  <p className="text-xs text-gray-400 truncate">{p.email} · {p.rol}</p>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Pizarra Tab — Feed Stream con publicación server-side, comentarios + encuestas
// ============================================================================
const PizarraDocente = ({ API, user, info }) => {
  const [client, setClient] = useState(null);
  const [feed, setFeed] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  const headersRef = useRef({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  // 1) Conectar Stream Feeds
  useEffect(() => {
    let mounted = true;
    let fc = null;
    (async () => {
      try {
        const tk = await axios.get(`${API}/stream-feeds/token`, { headers: headersRef.current });
        if (!mounted) return;
        const data = tk.data;
        fc = new FeedsClient(data.api_key);
        await fc.connectUser(
          { id: data.user_id, name: data.user_name, image: data.user_image || undefined },
          data.token
        );
        if (!mounted) return;
        setClient(fc);
        setMe({ id: data.user_id, name: data.user_name, image: data.user_image });
      } catch (e) {
        console.error('Feeds connect error:', e);
      }
    })();
    return () => {
      mounted = false;
      try { fc?.disconnectUser?.(); } catch (_) {}
    };
  }, [API]);

  // 2) Abrir feed de la sala
  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    (async () => {
      try {
        const f = client.feed('user', FEED_OWNER);
        await f.getOrCreate({ watch: true });
        if (cancelled) return;
        setFeed(f);

        const q = await client.queryActivities({
          filter: { feeds: [FEED_FQ_ID] },
          sort: [{ field: 'created_at', direction: -1 }],
          limit: 30,
        });
        if (cancelled) return;
        setActivities(q?.activities || []);
        setLoading(false);

        const unsub = f.state.subscribe((s) => {
          const incoming = s.activities || [];
          setActivities(prev => {
            const ids = new Set(prev.map(a => a.id));
            const news = incoming.filter(a => !ids.has(a.id));
            if (news.length === 0) return prev;
            return [...news, ...prev];
          });
        });
        return () => unsub?.();
      } catch (e) {
        console.error('Open feed error:', e);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client]);

  const reloadFeed = useCallback(async () => {
    if (!client) return;
    try {
      const q = await client.queryActivities({
        filter: { feeds: [FEED_FQ_ID] },
        sort: [{ field: 'created_at', direction: -1 }],
        limit: 30,
      });
      const incoming = q?.activities || [];
      // IMPORTANTE: no reemplazar la lista entera. Si por algún motivo queryActivities
      // devuelve vacío (token expirado, request cancelado, etc.) borraría todo el feed
      // en pantalla. En su lugar, hacemos MERGE: traemos nuevas + actualizamos las que
      // ya están si Stream devolvió una versión más fresca.
      if (incoming.length === 0) return; // protección crítica
      setActivities((prev) => {
        const byId = new Map(prev.map((a) => [a.id, a]));
        for (const a of incoming) byId.set(a.id, a);
        return Array.from(byId.values());
      });
    } catch (_) {}
  }, [client]);

  // Polling fallback: el realtime de Stream Feeds a veces tarda o no llega para
  // otros profesores que están viendo el feed cuando alguien publica. Refrescamos
  // cada 15s para garantizar que los posts nuevos aparezcan sin necesidad de
  // salir y volver a entrar a la pestaña.
  useEffect(() => {
    if (!client) return;
    const t = setInterval(() => { reloadFeed(); }, 15000);
    // También refrescar cuando el tab vuelve a estar visible (cambio de pestaña/app).
    const onVis = () => { if (document.visibilityState === 'visible') reloadFeed(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [client, reloadFeed]);

  // Publicar
  const handleCreatePost = useCallback(async (text, imageFile) => {
    const attachments = [];
    if (imageFile && client) {
      try {
        const up = await client.uploadImage({ file: imageFile });
        // El SDK Feeds devuelve { file: <url>, ... } (NO { url }). Probamos varios
        // shapes por seguridad — antes leíamos `up?.url` y siempre era undefined,
        // por eso las imágenes nunca aparecían en el feed.
        const url = up?.file || up?.url || up?.data?.file;
        if (url) {
          attachments.push({ type: 'image', asset_url: url, image_url: url });
        } else {
          console.warn('uploadImage sin URL en payload:', up);
          toast.error('No se pudo subir la imagen (sin URL)');
        }
      } catch (err) {
        console.error('uploadImage error:', err);
        toast.error('No se pudo subir la imagen');
      }
    }
    let activityId = null;
    try {
      const r = await axios.post(
        `${API}/area-profesores/feed/post`,
        { text: text || '', attachments },
        { headers: headersRef.current }
      );
      activityId = r?.data?.activity_id;
    } catch (e) {
      throw new Error(e?.response?.data?.detail || 'No se pudo publicar');
    }
    toast.success('Publicado en la pizarra');

    // ⚡ Update OPTIMISTA — mostrar el post al instante en la UI sin esperar al
    // round-trip de Stream Feeds (que tiene consistencia eventual ~700ms).
    // Antes, el post recién aparecía al salir y volver a entrar al tab.
    if (activityId) {
      const optimisticActivity = {
        id: activityId,
        type: 'post',
        text: text || '',
        attachments,
        user: me ? { id: me.id, name: me.name, image: me.image } : undefined,
        created_at: new Date().toISOString(),
        custom: { scope: 'area_profesores' },
        reaction_groups: {},
        latest_reactions: [],
        comment_count: 0,
      };
      setActivities(prev => {
        if (prev.some(a => a.id === activityId)) return prev;
        return [optimisticActivity, ...prev];
      });
    }

    // Refresh diferido para reconciliar con el estado autoritativo del server
    // (incluye timestamps reales, enriched user, etc.). 800ms cubre la latencia
    // de propagación del feed.
    setTimeout(() => { reloadFeed(); }, 800);

    // Push best-effort
    try {
      await axios.post(
        `${API}/area-profesores/feed/notify-new-post`,
        { text: text || '', activity_id: activityId, has_image: attachments.length > 0 },
        { headers: headersRef.current }
      );
    } catch (_) {}

    // Notificar a los profesores mencionados con @ (best-effort).
    // Detectamos handles "@Nombre_Apellido" en el texto.
    const mentions = (text || '').match(/@([\w.]+)/g);
    if (mentions && mentions.length > 0 && activityId) {
      try {
        const handles = mentions.map((m) => m.slice(1).replace(/_/g, ' '));
        await axios.post(
          `${API}/stream-feeds/mention-notify`,
          { activity_id: activityId, mentioned_names: handles, context: 'sala_docente' },
          { headers: headersRef.current }
        );
      } catch (_) {}
    }
  }, [client, API, reloadFeed, me]);

  const handleDelete = useCallback(async (activity) => {
    if (!info?.puede_moderar) return;
    if (!window.confirm('¿Borrar este post?')) return;
    try {
      await axios.post(
        `${API}/stream-feeds/delete-activity`,
        { activity_id: activity.id, feed_id: FEED_FQ_ID },
        { headers: headersRef.current }
      );
      setActivities(prev => prev.filter(a => a.id !== activity.id));
      toast.success('Post eliminado');
    } catch (_) {
      toast.error('No se pudo borrar');
    }
  }, [API, info]);

  const handleLike = useCallback(async (activity) => {
    if (!client) return;
    try {
      const isLiked = activity.own_reactions?.some(r => r.type === 'like');
      if (isLiked) {
        await client.deleteActivityReaction({ activity_id: activity.id, type: 'like' });
      } else {
        await client.addActivityReaction({ activity_id: activity.id, type: 'like' });
      }
      await reloadFeed();
    } catch (_) {}
  }, [client, reloadFeed]);

  const handleTogglePin = useCallback(async (activity) => {
    if (!info?.puede_moderar) return;
    const isPinned = !!activity.custom?.pinned;
    try {
      await axios.post(
        `${API}/stream-feeds/pin-activity`,
        { activity_id: activity.id, pin: !isPinned },
        { headers: headersRef.current }
      );
      toast.success(isPinned ? 'Anuncio desfijado' : '📌 Anuncio fijado');
      await reloadFeed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar el pin');
    }
  }, [API, info, reloadFeed]);

  // Ordenar: pinned primero (por pinned_at desc), después por created_at desc
  const sortedActivities = useMemo(() => {
    // Helper: normalizar a timestamp (number) para comparar de forma robusta.
    // Stream Feeds a veces devuelve created_at como Date object y a veces como
    // string ISO. localeCompare solo funciona con strings, por eso antes crasheaba
    // con "(b.created_at || '').localeCompare is not a function".
    const toTs = (v) => {
      if (!v) return 0;
      if (v instanceof Date) return v.getTime();
      if (typeof v === 'number') return v;
      const t = new Date(v).getTime();
      return isNaN(t) ? 0 : t;
    };
    const list = [...activities];
    list.sort((a, b) => {
      const aP = !!a.custom?.pinned;
      const bP = !!b.custom?.pinned;
      if (aP && !bP) return -1;
      if (!aP && bP) return 1;
      if (aP && bP) {
        return toTs(b.custom?.pinned_at) - toTs(a.custom?.pinned_at);
      }
      return toTs(b.created_at) - toTs(a.created_at);
    });
    return list;
  }, [activities]);

  return (
    <div className="p-4 sm:p-6">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
        </div>
      ) : (
        <>
          {/* Widget: Mis Capacitaciones / Auto-capacitaciones activas (mismo diseño que el Dashboard) */}
          <CapacitacionesProfesorWidget />

          <PizarraComposer
            onSubmit={handleCreatePost}
            userName={me?.name}
            userImage={me?.image}
            API={API}
            canCreatePoll={!!info}
            onPollCreated={reloadFeed}
          />
          {activities.length === 0 ? (
            <Card className="bg-gray-50 border-dashed mt-4">
              <CardContent className="p-8 text-center text-gray-500">
                <MessageSquare className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm">Todavía no hay anuncios en el muro.</p>
                <p className="text-xs">Sé el primero en compartir algo con el equipo.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3 mt-4">
              {sortedActivities.map(a => (
                <PostCardErrorBoundary key={a.id} activityId={a.id}>
                  <PostCard
                    activity={a}
                    isMine={a.user?.id === me?.id}
                    canModerate={info?.puede_moderar}
                    client={client}
                    onDelete={handleDelete}
                    onLike={handleLike}
                  onTogglePin={handleTogglePin}
                  onUpdated={reloadFeed}
                  />
                </PostCardErrorBoundary>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Composer
const PizarraComposer = ({ onSubmit, userName, userImage, API, canCreatePoll, onPollCreated }) => {
  const [text, setText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const textareaRef = useRef(null);

  // Lista de profesores+admins (con user.id real) para usar como audiencia
  // dirigida al crear encuestas. Se carga una sola vez al montar.
  const [audienceOptions, setAudienceOptions] = useState([]);
  useEffect(() => {
    if (!canCreatePoll) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/area-profesores/profesores-disponibles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setAudienceOptions((data.profesores || []).map((p) => ({
          id: p.id,
          name: p.nombre,
          role: p.rol,
        })));
      } catch (_e) {}
    })();
    return () => { cancelled = true; };
  }, [API, canCreatePoll]);

  // Autocomplete de @menciones — limitado a profesores y admins (la audiencia de Sala Docente)
  const [allUsers, setAllUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/stream-chat/users`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json();
        // Solo profesores y admins — la audiencia de Sala Docente
        const filtered = (data.users || []).filter((u) => {
          const r = (u.rol || u.role || '').toLowerCase();
          return r === 'profesor' || r === 'admin';
        });
        if (!cancelled) setAllUsers(filtered);
      } catch (_e) {}
    })();
    return () => { cancelled = true; };
  }, [API]);

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    const caret = e.target.selectionStart || 0;
    const before = val.slice(0, caret);
    const match = before.match(/@([\w.]*)$/);
    if (match) {
      setMentionQuery({ start: caret - match[0].length, query: match[1].toLowerCase() });
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const filteredUsers = mentionQuery
    ? allUsers
        .filter((u) => {
          const q = mentionQuery.query;
          if (!q) return true;
          return (
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
          );
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }))
        .slice(0, 30)
    : [];

  const pickMention = (u) => {
    if (!mentionQuery) return;
    const handle = (u.name || u.email || 'usuario').replace(/\s+/g, '_');
    const before = text.slice(0, mentionQuery.start);
    const after = text.slice(mentionQuery.start + 1 + mentionQuery.query.length);
    const newText = `${before}@${handle} ${after}`;
    setText(newText);
    setMentionQuery(null);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const pos = before.length + handle.length + 2;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 10);
  };

  const handleKeyDown = (e) => {
    if (!mentionQuery || filteredUsers.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((i) => Math.min(i + 1, filteredUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pickMention(filteredUsers[mentionIndex]);
    } else if (e.key === 'Escape') {
      setMentionQuery(null);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!text.trim() && !imageFile) return;
    setSubmitting(true);
    try {
      await onSubmit(text.trim(), imageFile);
      setText('');
      setImageFile(null);
      setImagePreview('');
    } catch (e) {
      toast.error(e.message || 'No se pudo publicar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          {userImage ? (
            <img src={userImage} alt={userName} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-sm">
              {(userName || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              placeholder="Escribí un anuncio para el equipo docente... (usá @ para mencionar)"
              className="w-full border-0 resize-none focus:outline-none text-sm placeholder-gray-400 min-h-[60px]"
              maxLength={2000}
              data-testid="pizarra-composer-text"
            />
            {/* Dropdown autocomplete de menciones */}
            {mentionQuery && filteredUsers.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="pizarra-mention-dropdown">
                {filteredUsers.map((u, idx) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pickMention(u); }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center gap-2 ${idx === mentionIndex ? 'bg-indigo-50' : ''}`}
                  >
                    {u.image ? (
                      <img src={u.image} alt={u.name} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-indigo-200 text-indigo-700 text-xs font-bold flex items-center justify-center">
                        {(u.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-800 truncate">{u.name}</p>
                      <p className="text-xs text-gray-400 truncate">{u.email}</p>
                    </div>
                    <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{u.rol || u.role}</Badge>
                  </button>
                ))}
              </div>
            )}
            {imagePreview && (
              <div className="relative inline-block mt-2">
                <img src={imagePreview} alt="preview" className="max-h-48 rounded-lg" />
                <button
                  onClick={() => { setImageFile(null); setImagePreview(''); }}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <label className="cursor-pointer text-gray-500 hover:text-indigo-600 text-sm flex items-center gap-1">
                  <ImageIcon className="h-4 w-4" />
                  <span>Imagen</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </label>
                {canCreatePoll && (
                  <button
                    type="button"
                    onClick={() => setShowPollModal(true)}
                    className="text-gray-500 hover:text-indigo-600 text-sm flex items-center gap-1"
                    data-testid="pizarra-create-poll-btn"
                  >
                    <BarChart3 className="h-4 w-4" />
                    <span>Encuesta</span>
                  </button>
                )}
              </div>
              <Button
                onClick={submit}
                disabled={submitting || (!text.trim() && !imageFile)}
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="pizarra-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Publicar
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
      {canCreatePoll && (
        <CreatePollModal
          isOpen={showPollModal}
          onClose={() => setShowPollModal(false)}
          onCreated={() => onPollCreated?.()}
          API={API}
          endpoint="/area-profesores/feed/polls"
          accent="indigo"
          title="Nueva Encuesta — Sala Docente"
          audienceOptions={audienceOptions}
        />
      )}
    </Card>
  );
};

// Helper: renderiza texto resaltando @menciones
const renderTextWithMentions = (text) => {
  if (!text) return null;
  const parts = text.split(/(@[\w.]+)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('@')) {
      return (
        <span key={idx} className="text-indigo-600 font-semibold bg-indigo-50 rounded px-1">
          {part}
        </span>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
};

// ErrorBoundary local para aislar cada PostCard. Si una actividad maliciosa o
// malformada del feed rompe el render, NO debe tirar abajo toda la página
// Sala Docente (síntoma observado: pantalla blanca después de crear una encuesta).
class PostCardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('PostCard render error:', error, info, 'activity_id:', this.props.activityId);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Card className="border-l-4 border-l-amber-500 bg-amber-50">
          <CardContent className="p-3 text-xs text-amber-800">
            ⚠️ No se pudo mostrar esta publicación (datos inválidos). El equipo técnico fue notificado.
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}

// Post Card
const PostCard = ({ activity, isMine, canModerate, client, onDelete, onLike, onTogglePin, onUpdated }) => {
  // Defensive: garantizar que los campos críticos sean del tipo esperado.
  // Stream Feeds a veces devuelve estructuras inesperadas para activities tipo
  // 'poll' creadas server-side (sin own_reactions, sin attachments, etc.)
  const attachments = Array.isArray(activity.attachments) ? activity.attachments : [];
  const imgAtt = attachments.find(a => a && (a.image_url || a.asset_url));
  const ownReactions = Array.isArray(activity.own_reactions) ? activity.own_reactions : [];
  const isLiked = ownReactions.some(r => r && r.type === 'like');
  const likeCount = activity.reaction_groups?.like?.count || 0;
  const createdAt = activity.created_at ? new Date(activity.created_at) : null;
  const isPinned = !!activity.custom?.pinned;

  // Edición
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(activity?.text || '');
  const [savingEdit, setSavingEdit] = useState(false);

  // Comentarios
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentCount = activity?.comment_count || 0;

  const loadComments = useCallback(async () => {
    if (!client || !activity?.id) return;
    setLoadingComments(true);
    try {
      const res = await client.getComments({ object_id: activity.id, object_type: 'activity', limit: 30 });
      setComments(res?.comments || []);
    } catch (err) {
      console.error('Error loading comments:', err);
    } finally {
      setLoadingComments(false);
    }
  }, [client, activity?.id]);

  useEffect(() => {
    if (showComments) loadComments();
  }, [showComments, loadComments]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || !client || !activity?.id) return;
    setSubmittingComment(true);
    try {
      await client.addComment({ comment: commentText.trim(), object_id: activity.id, object_type: 'activity' });
      setCommentText('');
      await loadComments();
      onUpdated?.();
    } catch (err) {
      console.error('Error comment:', err);
      toast.error('No se pudo comentar');
    } finally {
      setSubmittingComment(false);
    }
  };

  const startEdit = () => {
    setEditedText(activity?.text || '');
    setIsEditing(true);
  };
  const cancelEdit = () => {
    setIsEditing(false);
    setEditedText(activity?.text || '');
  };
  const saveEdit = async () => {
    if (!client || !activity?.id) return;
    const newText = editedText.trim();
    if (!newText) {
      toast.error('El texto no puede estar vacío');
      return;
    }
    if (newText === (activity?.text || '')) {
      setIsEditing(false);
      return;
    }
    setSavingEdit(true);
    try {
      if (typeof client.updateActivityPartial === 'function') {
        await client.updateActivityPartial({ id: activity.id, set: { text: newText } });
      } else if (typeof client.partialUpdateActivity === 'function') {
        await client.partialUpdateActivity({ id: activity.id, set: { text: newText } });
      } else {
        await client.updateActivity({
          id: activity.id,
          text: newText,
          attachments: activity.attachments || [],
          custom: activity.custom || {},
        });
      }
      activity.text = newText;
      setIsEditing(false);
      toast.success('Anuncio actualizado');
      onUpdated?.();
    } catch (err) {
      console.error('Error edit:', err);
      toast.error('No se pudo editar');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <Card
      className={`border shadow-sm ${isPinned ? 'border-amber-300 bg-amber-50/40' : 'border-gray-100'}`}
      data-testid={`pizarra-post-${activity.id}`}
    >
      <CardContent className="p-4">
        {isPinned && (
          <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-amber-700">
            <Pin className="w-3.5 h-3.5" />
            ANUNCIO FIJADO
          </div>
        )}
        <div className="flex items-start gap-3">
          {activity.user?.image ? (
            <img src={activity.user.image} alt={activity.user?.name} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold text-sm">
              {(activity.user?.name || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm text-gray-800 truncate">{activity.user?.name || 'Usuario'}</p>
                {createdAt && (
                  <p className="text-xs text-gray-400">
                    {createdAt.toLocaleDateString('es-UY', { timeZone: 'America/Montevideo' })} · {createdAt.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canModerate && (
                  <button
                    onClick={() => onTogglePin(activity)}
                    className={`p-1.5 rounded hover:bg-gray-100 ${isPinned ? 'text-amber-600' : 'text-gray-400 hover:text-amber-600'}`}
                    title={isPinned ? 'Desfijar' : 'Fijar anuncio'}
                    data-testid={`pin-btn-${activity.id}`}
                  >
                    {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                  </button>
                )}
                {isMine && !isEditing && (
                  <button
                    onClick={startEdit}
                    className="text-gray-400 hover:text-blue-500 p-1.5 rounded hover:bg-blue-50 transition-colors"
                    title="Editar anuncio"
                    data-testid={`pizarra-edit-${activity.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                {(isMine || canModerate) && (
                  <button
                    onClick={() => onDelete(activity)}
                    className="text-gray-400 hover:text-red-500 p-1.5 rounded hover:bg-gray-100"
                    title="Borrar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {isEditing ? (
              <div className="mt-3 space-y-2">
                <textarea
                  value={editedText}
                  onChange={(e) => setEditedText(e.target.value)}
                  rows={Math.min(8, Math.max(3, (editedText || '').split('\n').length + 1))}
                  maxLength={2000}
                  className="w-full px-3 py-2 border border-indigo-300 rounded-lg text-sm focus:border-indigo-500 focus:outline-none resize-none"
                  data-testid={`pizarra-edit-textarea-${activity.id}`}
                  autoFocus
                />
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-gray-400">
                    {editedText.length}/2000 · ✏️ Editando
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={cancelEdit}
                      disabled={savingEdit}
                      className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 font-medium"
                      data-testid={`pizarra-edit-cancel-${activity.id}`}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveEdit}
                      disabled={savingEdit || !editedText.trim()}
                      className="px-3 py-1.5 bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-300 text-white rounded-lg text-xs font-semibold flex items-center gap-1"
                      data-testid={`pizarra-edit-save-${activity.id}`}
                    >
                      {savingEdit ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> Guardando...</>
                      ) : (
                        <><Check className="w-3 h-3" /> Guardar</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              activity.text && (
                <p className="mt-1.5 text-sm text-gray-800 whitespace-pre-wrap break-words">
                  {renderTextWithMentions(activity.text)}
                </p>
              )
            )}

            {imgAtt?.image_url && (
              <img src={imgAtt.image_url} alt="post" className="mt-2 rounded-lg max-h-80 w-auto" />
            )}
            {activity.type === 'poll' && activity.custom?.poll_id && (
              <PollWidget
                pollId={activity.custom.poll_id}
                initialQuestion={activity.custom.question}
                initialOptions={activity.custom.options || []}
                accent="indigo"
              />
            )}
            <div className="flex items-center gap-4 mt-3 pt-2 border-t border-gray-100">
              <button
                onClick={() => onLike(activity)}
                className={`flex items-center gap-1 text-xs font-medium ${
                  isLiked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
                }`}
                data-testid={`pizarra-like-${activity.id}`}
              >
                <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                {likeCount > 0 && <span>{likeCount}</span>}
                <span className="hidden sm:inline">Me gusta</span>
              </button>
              <button
                onClick={() => setShowComments((v) => !v)}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-indigo-600"
                data-testid={`pizarra-toggle-comments-${activity.id}`}
              >
                <MessageCircle className="w-4 h-4" />
                {commentCount > 0 && <span>{commentCount}</span>}
                <span className="hidden sm:inline">Comentar</span>
              </button>
            </div>

            {/* Sección de comentarios */}
            {showComments && (
              <div className="bg-gray-50 -mx-4 -mb-4 mt-3 px-4 py-3 border-t border-gray-100">
                {loadingComments ? (
                  <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {comments.length === 0 && (
                      <p className="text-xs text-gray-500 italic text-center py-1">Sin comentarios todavía</p>
                    )}
                    {comments.map((c) => (
                      <div key={c.id} className="bg-white rounded-lg px-2.5 py-1.5 text-sm">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-gray-800 text-xs">{c.user?.name || 'Usuario'}</span>
                          {c.created_at && (
                            <span className="text-[10px] text-gray-400">
                              {new Date(c.created_at).toLocaleDateString('es-UY', { timeZone: 'America/Montevideo' })} · {new Date(c.created_at).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Montevideo' })}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm whitespace-pre-wrap break-words">
                          {renderTextWithMentions(c.comment || c.text || '')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                <form onSubmit={submitComment} className="flex items-center gap-2 mt-2">
                  <input
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Escribí un comentario..."
                    className="flex-1 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500"
                    data-testid={`pizarra-comment-input-${activity.id}`}
                    disabled={submittingComment}
                  />
                  <button
                    type="submit"
                    disabled={submittingComment || !commentText.trim()}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg disabled:opacity-50 hover:bg-indigo-700 transition-colors text-sm"
                    data-testid={`pizarra-comment-submit-${activity.id}`}
                  >
                    {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SalaDocente;
